import React from "react";
import {
    Card,
    Elevation,
    Tag,
    Button,
    H1,
    H2,
    H3,
    H4,
    Text,
    FormGroup,
    InputGroup,
    TextArea,
    HTMLSelect,
    NumericInput,
    Switch,
    Checkbox,
    RadioGroup,
    Radio,
    Icon,
    Intent,
    Spinner,
    NonIdealState,
    Callout,
    ProgressBar,
    Tooltip,
    Position,
    Divider,
    Tabs,
    Tab,
    Alert,
    Dialog,
    Classes,
    ControlGroup,
    MenuItem,
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { DateInput, TimePicker } from "@blueprintjs/datetime";
import { Suggest } from "@blueprintjs/select";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-enterprise";
import { inject, observer } from "mobx-react";
import { apiCallPost, apiCallGet } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import _ from "lodash";
import moment from "moment";
import AsyncSelWrap from "../../../micro/utils/AsyncSelWrap";
import SingleIDSelectWrapper from "../../../micro/utils/SingleIDSelectWrapper";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import SurgeryCalendar from "../../components/calendar/SurgeryCalendar";
import { nanoid } from "nanoid";
import { withRouter } from "react-router-dom";
import { getStatusMeta as getStatusMetaGlobal, getDisplayStatusMeta as getDisplayStatusMetaGlobal, toTitleCase as toTitleCaseGlobal } from "../../constants/statusColors";
import SxPriceEstimator from "../../components/SxPriceEstimator";
import { buildProcedureDischargeSummaryName } from "../../utils/procedureSummary";
import { filterBookingsForSlotMetrics, shouldIncludeBookingInSlotMetrics } from "../../utils/surgerySlotUtils";
import { getIsBilledLabel, getIsBilledPill } from "../../utils/isBilledPill";

// Set AG Grid license
LicenseManager.setLicenseKey("");

// Cross-page cache for master data to prevent duplicate API calls
if (typeof window !== "undefined") {
    window.__neoCache = window.__neoCache || {
        branches: null,
        payorPanels: null,
        printedNames: null,
        consultants: null,
    };
    window.__neoInflight = window.__neoInflight || {};
}

/** Normalize payor / panel strings for matching (trim + lowercase). */
const normalizePayorKey = (v) => (v == null ? "" : String(v)).trim().toLowerCase();

/** Cash / self-pay style payors in surgery payment master often use "Cash" while panels use "Self Pay", etc. */
const isCashLikePayorKey = (v) => {
    const n = normalizePayorKey(v);
    return !n || n === "cash" || n === "self pay" || n === "self-pay" || n === "patient pay";
};

const BUILTIN_CASH_PAYOR_PANEL = { _id: "__neo_builtin_cash__", panelName: "Cash" };

/** Insurance panel list from API may omit Cash; payment master rows default payor to Cash. */
const ensureCashPayorPanelInList = (panels) => {
    const list = Array.isArray(panels) ? [...panels] : [];
    const hasCash = list.some((p) => normalizePayorKey(p?.panelName) === "cash");
    if (!hasCash) {
        list.unshift(BUILTIN_CASH_PAYOR_PANEL);
    }
    return list;
};

class SurgeryBooking extends React.Component {
    _isMounted = false;
    _branchesPromise = null;
    _doctorsPromise = null;
    // Refs for focus management
    patientSearchRef = React.createRef();
    patientAddressRef = React.createRef();
    branchSelectRef = React.createRef();

    state = {
        // Surgeries List
        surgeries: [],
        filteredSurgeries: [],
        isLoading: true,
        isSaving: false,
        isPreparingCreateForm: false,
        isLoadingBranchesList: false,
        isLoadingDoctorsList: false,
        gridApi: null,
        columnApi: null,
        quickFilterText: "",
        doctorFilter: "",
        branchFilter: "",
        statusFilter: "",
        scheduledDateFilter: new Date(),
        selectedDoctorFilter: null,
        selectedBranchFilter: null,
        selectedStatusFilter: "All",
        showCreateForm: false,
        showEditForm: false,
        editingSurgery: null,

        // Patient Search
        patientSearchQuery: "",
        isSearchingPatients: false,
        searchResults: [],
        selectedPatient: null,
        patientAddress: "",
        patientName: "",
        patientPhone: "",
        patientDob: null,
        patientSex: "",
        isPatientDetailsEditable: false,
        oldSearching: false,
        oldSearchResults: [],

        // Surgery Details
        surgeryDetails: {
            group: "",
            eye: "",
            procedure: "",
            urgencyLevel: "routine",
            estimatedDuration: 60,
            selectedSurgeries: [], // Array of {id, name} objects
            payorPanel: "", // Selected payor panel
            cashSubsidy: "Cash", // Cash or Subsidy
        },

        // Doctor & Branch Selection
        selectedBranch: null,
        selectedDoctor: null,
        selectedAdvisedBy: null,
        availableDoctors: [],
        branches: [],

        // Calendar & Scheduling
        selectedDate: null,
        selectedTime: "",
        selectedTimeSlot: null,
        chosenTime: "",
        availableSlots: [],
        doctorCapacity: null,
        isLoadingCapacity: false,

        // Medical Metadata
        medicalInfo: {
            dietPreference: "regular",
            bedPreference: "Bed",
            isDiabetic: false,
            additionalNotes: "",
            allergies: "",
            currentMedications: "",
            anesthesia: "", // Overall anesthesia selection
        },

        // Communication
        whatsappNumber: "",
        enableWhatsappConfirmation: true,
        enableEmailConfirmation: true,

        // Booking State
        isBooking: false,
        isUpdating: false,
        bookingReference: "",
        showConfirmation: false,

        // Data Options
        surgeryTypes: [
            "Cataract Surgery",
            "Retinal Surgery",
            "Glaucoma Surgery",
            "Corneal Surgery",
            "Oculoplasty",
            "LASIK",
            "Vitrectomy",
            "Other",
        ],

        // Surgery Search
        printedNames: [],
        isLoadingPrintedNames: false,
        selectedSurgeriesFromAPI: [], // Changed to array for multi-select

        // New: Per-row surgeries for table input
        surgeryRows: [],

        // Aggregated specials: user-editable special discount (rupees); null => auto from selections
        specialDiscountRupees: null,
        specialDiscountManuallyEdited: false,

        // Surgery search state for relevance sorting
        surgerySearchQueries: {}, // Track search query per row index

        // Anesthesia options for surgery rows
        anesthesiaOptionsForSurgery: [],
        isLoadingAnesthesiaForSurgery: false,

        // Payor Panel
        payorPanels: [],
        isLoadingPayorPanels: false,
        selectedPayorPanel: null,

        urgencyOptions: [
            { value: "routine", label: "Routine" },
            { value: "emergency", label: "Emergency" },
        ],

        eyeOptions: [
            { value: "left", label: "Left Eye" },
            { value: "right", label: "Right Eye" },
            { value: "both", label: "Both Eyes" },
        ],

        cashSubsidyOptions: [
            { value: "Cash", label: "Cash" },
            { value: "Subsidy", label: "Subsidy" },
        ],

        // Results info
        totalItems: 0,
    };

    // Match schedule "day" to a date (numeric, full/abbr name, comma/space-separated)
    dayMatches = (scheduleDay, dateMoment) => {
        if (scheduleDay === null || typeof scheduleDay === "undefined") return false;
        const targetIdx = parseInt(moment(dateMoment).format("d"), 10); // 0..6
        const targetFull = moment(dateMoment).format("dddd").toLowerCase();
        const targetAbbr = moment(dateMoment).format("ddd").toLowerCase();

        const matchToken = (token) => {
            if (token === null || typeof token === "undefined") return false;
            if (typeof token === "number") return token === targetIdx;
            const str = String(token).trim().toLowerCase();
            if (str === "") return false;
            if (!isNaN(parseInt(str, 10))) {
                return parseInt(str, 10) === targetIdx;
            }
            return str === targetFull || str === targetAbbr;
        };

        if (typeof scheduleDay === "number") return scheduleDay === targetIdx;
        if (Array.isArray(scheduleDay))
            return scheduleDay.some((d) => this.dayMatches(d, dateMoment));

        const tokens = String(scheduleDay)
            .split(/[\s,|/]+/)
            .map((t) => t.trim())
            .filter(Boolean);
        return tokens.some(matchToken);
    };

    // ----- Branch Code Helpers -----
    deriveBranchShort3 = (name) => {
        try {
            const safe = String(name || "BR").trim();
            const norm = safe.toLowerCase();
            // Explicit overrides for known branches
            if (norm.includes("barrackpore")) return "BKP";
            if (norm.includes("barasat")) return "BRS";
            const words = safe.split(/\s+/).filter(Boolean);
            let code = "";
            if (words.length > 1) {
                code = words
                    .map((w) => w[0])
                    .join("")
                    .toUpperCase();
            } else {
                code = safe.slice(0, 3).toUpperCase();
            }
            // Ensure exactly 3 letters
            code = (code || "BR").replace(/[^A-Z]/g, "");
            if (code.length >= 3) return code.slice(0, 3);
            return (code + "XXX").slice(0, 3);
        } catch (e) {
            return "BRX";
        }
    };

    componentDidMount() {
        this._isMounted = true;
        if (this._initDone) return;
        this._initDone = true;
        this.loadInitialData();
        this.applyPrefillFromQuery();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    applyPrefillFromQuery = async () => {
        try {
            const params = new URLSearchParams(this.props.location.search || "");
            const branchId = params.get("branchId");
            const branchName = params.get("branchName");
            const doctorId = params.get("doctorId");
            const doctorName = params.get("doctorName");
            const dateStr = params.get("date");
            const timeFrame = params.get("timeFrame");

            // If nothing to prefill and not explicit create, return
            const mode = params.get("mode");
            if (!branchId && !doctorId && !dateStr && !timeFrame && mode !== "create")
                return;

            // Ensure branches and doctors lists are loaded
            if (!this.state.branches || this.state.branches.length === 0) {
                await this.loadBranches();
            }
            if (
                !this.state.availableDoctors ||
                this.state.availableDoctors.length === 0
            ) {
                await this.loadAvailableDoctors(branchId);
            }

            const selectedBranch = branchId
                ? this.state.branches.find((b) => b._id === branchId) || {
                    _id: branchId,
                    name: branchName || "",
                    branchCode: "",
                }
                : this.state.selectedBranch;
            const selectedDoctor = doctorId
                ? { _id: doctorId, name: doctorName || "" }
                : this.state.selectedDoctor;

            const selectedDate = dateStr
                ? moment(dateStr, "YYYY-MM-DD").toDate()
                : this.state.selectedDate;

            // Open create form if patient is not yet selected; user will search after landing
            const prefills = {
                selectedBranch,
                selectedDoctor,
                selectedDate,
            };

            if (selectedBranch && selectedDoctor && selectedDate && timeFrame) {
                const startTime = timeFrame.split(" - ")[0];
                prefills.selectedTimeSlot = {
                    time: startTime,
                    timeFrame,
                    capacity: 0,
                    bookedCount: 0,
                };
                prefills.selectedTime = startTime;
                prefills.chosenTime = moment(startTime, "hh:mm A").format("HH:mm");
            }

            this.openCreateForm(prefills);
        } catch (e) {
            console.warn("Failed to prefill from query", e);
        }
    };

    hasData = (list) => Array.isArray(list) && list.length > 0;

    areCriticalMastersReady = () => {
        return (
            this.hasData(this.state.branches) &&
            this.hasData(this.state.availableDoctors) &&
            this.hasData(this.state.payorPanels) &&
            this.hasData(this.state.printedNames) &&
            this.hasData(this.state.anesthesiaOptionsForSurgery)
        );
    };

    ensureMasterDropdownsReady = async () => {
        const tasks = [];
        if (!this.hasData(this.state.branches)) {
            tasks.push(this.loadBranches());
        }
        if (!this.hasData(this.state.availableDoctors)) {
            tasks.push(this.loadAvailableDoctors());
        }
        if (!this.hasData(this.state.payorPanels)) {
            tasks.push(this.loadPayorPanels());
        }
        if (!this.hasData(this.state.printedNames)) {
            tasks.push(this.loadPrintedNames());
        }
        if (!this.hasData(this.state.anesthesiaOptionsForSurgery)) {
            tasks.push(this.loadAnesthesiaOptionsForSurgery());
        }
        if (!tasks.length) {
            return;
        }
        try {
            await Promise.allSettled(tasks);
        } catch (err) {
            console.error("Failed to ensure dropdown data", err);
        }
    };

    focusPatientSearch = () => {
        setTimeout(() => {
            if (
                this.patientSearchRef.current &&
                typeof this.patientSearchRef.current.focus === "function"
            ) {
                this.patientSearchRef.current.focus();
            }
        }, 100);
    };

    openCreateForm = (extraState = {}, options = {}) => {
        const { focusPatientSearch: shouldFocus = true } = options;
        const mastersReady = this.areCriticalMastersReady();
        // Fresh catalog: cached printedNames hides new Surgery Payment Master rows
        this.loadPrintedNames(true).catch(() => {});
        this.setState(
            {
                showCreateForm: true,
                isPreparingCreateForm: !mastersReady,
                ...extraState,
            },
            () => {
                if (mastersReady) {
                    if (shouldFocus) {
                        this.focusPatientSearch();
                    }
                    return;
                }
                this.ensureMasterDropdownsReady()
                    .catch((err) => {
                        console.error("Failed to preload dropdown masters", err);
                    })
                    .finally(() => {
                        if (!this._isMounted) {
                            return;
                        }
                        this.setState({ isPreparingCreateForm: false }, () => {
                            if (shouldFocus) {
                                this.focusPatientSearch();
                            }
                        });
                    });
            }
        );
    };

    loadInitialData = async () => {
        try {
            // Set today's date first
            const today = new Date();
            this.setState({ scheduledDateFilter: today });

            // Fire surgery loading independently — it doesn't gate the
            // create-form dropdowns and is heavy (30-day history + future).
            this.loadSurgeries(today);

            // Load all master dropdown data in parallel (these gate the create form)
            const initialLoads = await Promise.allSettled([
                this.loadBranches(),
                this.loadPayorPanels(),
                this.loadAnesthesiaOptionsForSurgery(),
                this.loadAvailableDoctors(),
                this.loadPrintedNames(),
            ]);

            // If any of the critical dropdown lists failed, retry them individually so the UI never stays empty
            const branchLoadFailed = initialLoads[0]?.status === "rejected";
            const doctorLoadFailed = initialLoads[3]?.status === "rejected";
            if (branchLoadFailed || !(this.state.branches || []).length) {
                try {
                    await this.loadBranches();
                } catch (e) {
                    console.error("Retry loadBranches failed", e);
                }
            }
            if (doctorLoadFailed || !(this.state.availableDoctors || []).length) {
                try {
                    await this.loadAvailableDoctors();
                } catch (e) {
                    console.error("Retry loadAvailableDoctors failed", e);
                }
            }

            // Prefill branch filter from global selection
            const preferredName = this.props.authStore?.selectedBranchName;
            if (
                preferredName &&
                Array.isArray(this.state.branches) &&
                this.state.branches.length
            ) {
                const norm = (s) =>
                    String(s || "")
                        .trim()
                        .toLowerCase();
                const match = this.state.branches.find(
                    (b) => norm(b.name) === norm(preferredName)
                );
                if (match) {
                    this.setState(
                        {
                            selectedBranchFilter: match.name,
                            branchFilter: match.name,
                        },
                        () => this.applyFilters() // Apply client-side filters
                    );
                }
            }
        } catch (error) {
            console.error("Error loading initial data:", error);
            cogoToast.error("Failed to load initial data");
        }
    };

    loadAnesthesiaOptionsForSurgery = async () => {
        try {
            this.setState({ isLoadingAnesthesiaForSurgery: true });
            if (window.__neoCache?.anesthesiaOptionsForSurgery) {
                const options = window.__neoCache.anesthesiaOptionsForSurgery;
                this.setState({
                    anesthesiaOptionsForSurgery: options,
                    isLoadingAnesthesiaForSurgery: false,
                });
                return;
            }
            if (window.__neoInflight?.getAnesthesiaForSurgery) {
                const resp = await window.__neoInflight.getAnesthesiaForSurgery;
                const items = Array.isArray(resp?.data)
                    ? resp.data
                    : Array.isArray(resp)
                        ? resp
                        : [];
                const options = items
                    .filter(
                        (item) =>
                            item && typeof item.name === "string" && item.name.trim().length > 0
                    )
                    .map((item) => ({
                        value: item.name,
                        label: item.name,
                        rate: item.rate || 0,
                    }));
                this.setState({
                    anesthesiaOptionsForSurgery: options,
                    isLoadingAnesthesiaForSurgery: false,
                });
                if (typeof window !== "undefined")
                    window.__neoCache.anesthesiaOptionsForSurgery = options;
                return;
            }
            window.__neoInflight.getAnesthesiaForSurgery = apiCallPost(
                `/v3/${this.props.authStore.prefix}/neoSxAnesthesiaPaymentMaster/getAll`,
                {}
            );
            const response = await window.__neoInflight.getAnesthesiaForSurgery;

            const items = Array.isArray(response?.data)
                ? response.data
                : Array.isArray(response)
                    ? response
                    : [];
            const options = items
                .filter(
                    (item) =>
                        item && typeof item.name === "string" && item.name.trim().length > 0
                )
                .map((item) => ({
                    value: item.name,
                    label: item.name,
                    rate: item.rate || 0,
                }));

            this.setState({
                anesthesiaOptionsForSurgery: options,
                isLoadingAnesthesiaForSurgery: false,
            });
            if (typeof window !== "undefined")
                window.__neoCache.anesthesiaOptionsForSurgery = options;
        } catch (error) {
            console.error("Error loading anesthesia options for surgery:", error);
            this.setState({ isLoadingAnesthesiaForSurgery: false });
            // Fallback to empty array - not critical for surgery booking
        }
    };

    loadSurgeries = async (targetDate = null) => {
        try {
            this.setState({ isLoading: true });
            cogoToast.loading("Loading today's surgeries...");

            const filterDate =
                targetDate || this.state.scheduledDateFilter || new Date();
            const todayStr = moment(filterDate).format("YYYY-MM-DD");
            const yesterdayStr = moment(filterDate)
                .subtract(1, "day")
                .format("YYYY-MM-DD");
            const pastStartStr = moment(filterDate)
                .subtract(30, "day")
                .format("YYYY-MM-DD");
            const tomorrowStr = moment(filterDate).add(1, "day").format("YYYY-MM-DD");

            // 1) Fast-path: fetch only today's surgeries first
            const todayResp = await apiCallPost(
                `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`,
                {
                    startDate: todayStr,
                    endDate: todayStr,
                    page: 1,
                    limit: 2000,
                    sortBy: "scheduledTime",
                    sortOrder: "asc",
                    mode: "list",
                }
            );
            const todayItems = todayResp?.data?.items ? todayResp.data.items : [];
            const todayTotal = Number(
                todayResp?.data?.pagination?.total || todayItems.length || 0
            );

            this.setState(
                {
                    surgeries: todayItems,
                    totalItems: todayTotal,
                    isLoading: false,
                },
                () => {
                    this.applyFilters();
                    cogoToast.success(
                        `${todayItems.length} surgeries loaded for ${moment(filterDate).format(
                            "DD/MM/YYYY"
                        )}`
                    );
                }
            );

            // 2) Background: fetch past 30 days (excluding today) and future (from tomorrow), paginated
            const payloadPast = {
                startDate: pastStartStr,
                endDate: yesterdayStr,
                sortBy: "scheduledTime",
                sortOrder: "asc",
                mode: "list",
            };
            const payloadFuture = {
                startDate: tomorrowStr,
                sortBy: "scheduledTime",
                sortOrder: "asc",
                mode: "list",
            };

            // Kick off both in parallel
            const [pastResult, futureResult] = await Promise.all([
                this.fetchAllSurgeriesPaged(payloadPast, 2000).catch(() => ({
                    items: [],
                    total: 0,
                })),
                this.fetchAllSurgeriesPaged(payloadFuture, 2000).catch(() => ({
                    items: [],
                    total: 0,
                })),
            ]);

            // Merge in background results and re-apply filters silently
            const mergedItems = [
                ...todayItems,
                ...(pastResult.items || []),
                ...(futureResult.items || []),
            ];
            this.mergeSurgeriesIntoState(
                mergedItems,
                pastResult.total + futureResult.total + todayTotal
            );
        } catch (err) {
            cogoToast.error("Failed to load surgeries");
            console.error("Failed to load surgeries:", err);
            this.setState({ isLoading: false });
            this.initializeDummySurgeries();
        }
    };

    // Fetch all pages for a given payload, with limited concurrency
    fetchAllSurgeriesPaged = async (
        payloadBase,
        pageSize = 1000,
        concurrency = 3
    ) => {
        const first = await apiCallPost(
            `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`,
            {
                ...payloadBase,
                page: 1,
                limit: pageSize,
            }
        );
        const firstItems = first?.data?.items ? first.data.items : [];
        const pagination = first?.data?.pagination || {};
        const pages = Number(pagination.pages || 1);
        const total = Number(pagination.total || firstItems.length || 0);

        if (pages <= 1) {
            return { items: firstItems, total };
        }

        const remainingPages = Array.from({ length: pages - 1 }, (_, i) => i + 2);

        // Process in batches to avoid flooding the server
        const results = [...firstItems];
        for (let i = 0; i < remainingPages.length; i += concurrency) {
            const batch = remainingPages.slice(i, i + concurrency);
            const batchResponses = await Promise.all(
                batch.map((p) =>
                    apiCallPost(
                        `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`,
                        {
                            ...payloadBase,
                            page: p,
                            limit: pageSize,
                        }
                    ).catch(() => ({ data: { items: [] } }))
                )
            );
            batchResponses.forEach((resp) => {
                const items = resp?.data?.items ? resp.data.items : [];
                if (Array.isArray(items) && items.length) results.push(...items);
            });
        }

        return { items: results, total };
    };

    // Merge items into state.surgeries without duplicates and update filtered view
    mergeSurgeriesIntoState = (newItems, totalCountOverride = null) => {
        this.setState(
            (prev) => {
                const byId = new Map();
                const push = (s) => {
                    const key =
                        s?.id ||
                        s?._id ||
                        s?.bookingReference ||
                        `${s?.patientId}-${s?.scheduledDate}-${s?.scheduledTime}`;
                    if (!byId.has(key)) byId.set(key, s);
                    else byId.set(key, { ...byId.get(key), ...s });
                };
                (prev.surgeries || []).forEach(push);
                (newItems || []).forEach(push);
                const surgeries = Array.from(byId.values());
                return { surgeries, totalItems: totalCountOverride ?? prev.totalItems };
            },
            () => this.applyFilters()
        );
    };

    initializeDummySurgeries = () => {
        const dummySurgeries = [
            {
                id: "1",
                patientId: "NCT0000000001",
                patientGenericPid: "NCT0000000001",
                patientName: "John Doe",
                patientPhone: "+91 98765 43210",
                type: "Cataract Surgery",
                group: "General",
                eye: "left",
                procedure: "Phacoemulsification with IOL",
                urgencyLevel: "routine",
                estimatedDuration: 60,
                doctorId: "doc_1",
                doctorName: "Dr. Ammar",
                branchId: "branch_1",
                branchName: "Newtown Branch",
                scheduledDate: "2025-01-15T10:00:00.000Z",
                scheduledTime: "10:00 AM",
                timeFrame: "10:00 AM - 10:30 AM",
                slotCapacity: 6,
                slotBookedCount: 2,
                dietPreference: "regular",
                bedPreference: "general",
                isDiabetic: false,
                additionalNotes: "",
                allergies: "",
                currentMedications: "",
                whatsappNumber: "+91 98765 43210",
                enableWhatsappConfirmation: true,
                enableEmailConfirmation: true,
                bookingReference: "SX12345678",
                status: "confirmed",
                createdById: "user_1",
                createdByName: "System",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            },
            {
                id: "2",
                patientId: "NCT0000000002",
                patientGenericPid: "NCT0000000002",
                patientName: "Jane Smith",
                patientPhone: "+91 98765 43211",
                type: "Retinal Surgery",
                group: "Specialty",
                eye: "right",
                procedure: "Vitrectomy with membrane peeling",
                urgencyLevel: "urgent",
                estimatedDuration: 120,
                doctorId: "doc_2",
                doctorName: "Dr. Smith",
                branchId: "branch_2",
                branchName: "Park Street Branch",
                scheduledDate: "2025-01-16T14:00:00.000Z",
                scheduledTime: "2:00 PM",
                timeFrame: "2:00 PM - 2:30 PM",
                slotCapacity: 4,
                slotBookedCount: 1,
                dietPreference: "diabetic",
                bedPreference: "private",
                isDiabetic: true,
                additionalNotes: "Patient has diabetes",
                allergies: "Penicillin",
                currentMedications: "Metformin, Insulin",
                whatsappNumber: "+91 98765 43211",
                enableWhatsappConfirmation: true,
                enableEmailConfirmation: false,
                bookingReference: "SX87654321",
                status: "confirmed",
                createdById: "user_1",
                createdByName: "System",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            },
        ];

        this.setState(
            {
                surgeries: dummySurgeries,
                filteredSurgeries: dummySurgeries,
                isLoading: false,
            },
            () => {
                // Apply any existing filters after loading
                this.applyFilters();
            }
        );
    };

    loadBranches = async () => {
        if ((this.state.branches || []).length > 0) {
            return;
        }
        if (this._branchesPromise) {
            return this._branchesPromise;
        }
        this.setState({ isLoadingBranchesList: true });
        this._branchesPromise = (async () => {
            try {
                const cachedBranches = window.__neoCache?.branches;
                if (Array.isArray(cachedBranches) && cachedBranches.length > 0) {
                    if (this._isMounted) {
                        this.setState({ branches: cachedBranches });
                    }
                    return;
                }
                if (window.__neoInflight?.getAllSites) {
                    const cached = await window.__neoInflight.getAllSites;
                    if (Array.isArray(cached) && cached.length > 0) {
                        if (this._isMounted) {
                            this.setState({ branches: cached });
                        }
                        return;
                    }
                }

                window.__neoInflight.getAllSites = apiCallPost(
                    `/rai/${this.props.authStore.prefix}/getAllSites`,
                    {}
                );
                const response = await window.__neoInflight.getAllSites;

                const branches = (response || []).map((item, index) => {
                    if (typeof item === "string") {
                        const name = item;
                        return {
                            _id: `branch_${index}`,
                            name,
                            branchCode: this.deriveBranchShort3(name),
                        };
                    }
                    const _id = item?._id || item?.id || `branch_${index}`;
                    const name = item?.name || item?.branchName || item?.siteName || "";
                    const branchCode =
                        item?.branchCode ||
                        item?.code ||
                        item?.shortCode ||
                        this.deriveBranchShort3(name);
                    return { _id, name, branchCode };
                });

                if (this._isMounted) {
                    this.setState({ branches });
                }
                if (typeof window !== "undefined") {
                    window.__neoCache.branches = branches;
                }
            } catch (error) {
                console.error("Error loading branches:", error);
                cogoToast.error("Failed to load branches");
                if (this._isMounted) {
                    this.setState({
                        branches: [
                            {
                                _id: "1",
                                name: "Newtown Branch",
                                branchCode: this.deriveBranchShort3("Newtown Branch"),
                            },
                            {
                                _id: "2",
                                name: "Park Street Branch",
                                branchCode: this.deriveBranchShort3("Park Street Branch"),
                            },
                            {
                                _id: "3",
                                name: "Salt Lake Branch",
                                branchCode: this.deriveBranchShort3("Salt Lake Branch"),
                            },
                            {
                                _id: "4",
                                name: "Howrah Branch",
                                branchCode: this.deriveBranchShort3("Howrah Branch"),
                            },
                        ],
                    });
                }
            } finally {
                if (this._isMounted) {
                    this.setState({ isLoadingBranchesList: false });
                }
                this._branchesPromise = null;
            }
        })();
        return this._branchesPromise;
    };

    // Helper method to clear printedNames cache (for debugging)
    clearPrintedNamesCache = () => {
        if (typeof window !== "undefined") {
            delete window.__neoCache.printedNames;
            delete window.__neoInflight.getAllPrintedNames;
        }
    };

    loadPrintedNames = async (forceRefresh = false) => {
        try {
            if (forceRefresh && typeof window !== "undefined") {
                if (window.__neoCache) delete window.__neoCache.printedNames;
                if (window.__neoInflight) delete window.__neoInflight.getAllPrintedNames;
            }

            // Stale cache hides rows added in Surgery Payment Master until full reload
            if (!forceRefresh && window.__neoCache?.printedNames) {
                this.setState({
                    printedNames: window.__neoCache.printedNames,
                    isLoadingPrintedNames: false,
                });
                return;
            }

            this.setState({ isLoadingPrintedNames: true });

            // Always load all surgery types - we'll filter client-side
            // This provides better performance and allows for dynamic filtering
            if (window.__neoInflight?.getAllPrintedNames) {
                const response = await window.__neoInflight.getAllPrintedNames;
                const printedNamesData = response?.data?.items || [];
                const printedNames = printedNamesData.map((item) => ({
                    _id: item._id,
                    printedName: item.printedName || item.surgeryName,
                    payor: item.payor,
                    group: item.group,
                    // Financial data from surgery payment master
                    surgeryRate: item.surgeryRate || 0,
                    anaestheticCharges: item.anaestheticCharges || 0,
                    tissueProcessing: item.tissueProcessing || 0,
                    rate: item.rate || 0, // Package cost
                    taxApplicable: item.taxApplicable || 0,
                    implantTax: item.implantTax || 0,
                    procedureCost: item.procedureCost || 0,
                    implantCost: item.implantCost || 0,
                    femto: item.femto || 0,
                    specialDiscount: item.specialDiscount || 0,
                    mouDiscount: item.mouDiscount || 0,
                    iolDiscount: item.iolDiscount || 0,
                }));
                this.setState({ printedNames, isLoadingPrintedNames: false });
                if (typeof window !== "undefined")
                    window.__neoCache.printedNames = printedNames;
                return;
            }

            window.__neoInflight.getAllPrintedNames = apiCallGet(
                `/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/getAll?limit=10000&offset=0&isActive=true`
            );
            const response = await window.__neoInflight.getAllPrintedNames;

            // Handle the API response structure - API returns items array
            const printedNamesData = response?.data?.items || [];

            // Transform for Suggest component - include payor information and financial data
            const printedNames = printedNamesData.map((item) => ({
                _id: item._id,
                printedName: item.printedName || item.surgeryName,
                payor: item.payor,
                group: item.group,
                // Financial data from surgery payment master
                surgeryRate: item.surgeryRate || 0,
                anaestheticCharges: item.anaestheticCharges || 0,
                tissueProcessing: item.tissueProcessing || 0,
                rate: item.rate || 0, // Package cost
                taxApplicable: item.taxApplicable || 0,
                implantTax: item.implantTax || 0,
                procedureCost: item.procedureCost || 0,
                implantCost: item.implantCost || 0,
                femto: item.femto || 0,
                specialDiscount: item.specialDiscount || 0,
                mouDiscount: item.mouDiscount || 0,
                iolDiscount: item.iolDiscount || 0,
            }));

            this.setState({
                printedNames,
                isLoadingPrintedNames: false,
            });
        } catch (error) {
            console.error("Error loading printed names:", error);
            this.setState({ isLoadingPrintedNames: false });
            // Don't show error toast as this is not critical - fallback to regular dropdown
        }
    };

    loadPayorPanels = async () => {
        try {
            this.setState({ isLoadingPayorPanels: true });
            if (window.__neoCache?.payorPanels) {
                const payorPanels = ensureCashPayorPanelInList(window.__neoCache.payorPanels);
                this.setState({
                    payorPanels,
                    isLoadingPayorPanels: false,
                });
                return;
            }
            if (window.__neoInflight?.getAllPanels) {
                const resp = await window.__neoInflight.getAllPanels;
                const payorPanelsData = resp?.data || resp || [];
                const payorPanels = ensureCashPayorPanelInList(
                    payorPanelsData.map((item) => ({
                        _id: item._id,
                        panelName: item.panelName,
                    }))
                );
                this.setState({ payorPanels, isLoadingPayorPanels: false });
                if (typeof window !== "undefined")
                    window.__neoCache.payorPanels = payorPanels;
                return;
            }
            window.__neoInflight.getAllPanels = apiCallPost(
                `/v3/${this.props.authStore.prefix}/neoSxInsurancePanelMaster/getAll`,
                {}
            );
            const response = await window.__neoInflight.getAllPanels;

            // Handle the API response structure
            const payorPanelsData = response?.data || response || [];

            // Transform for dropdown - extract panelName
            const payorPanels = ensureCashPayorPanelInList(
                payorPanelsData.map((item) => ({
                    _id: item._id,
                    panelName: item.panelName,
                }))
            );

            this.setState({ payorPanels, isLoadingPayorPanels: false });
            if (typeof window !== "undefined")
                window.__neoCache.payorPanels = payorPanels;
        } catch (error) {
            console.error("Error loading payor panels:", error);
            this.setState({ isLoadingPayorPanels: false });
            // Fallback to dummy payor panels
            this.setState({
                payorPanels: ensureCashPayorPanelInList([
                    { _id: "1", panelName: "General Insurance" },
                    { _id: "2", panelName: "Corporate Panel" },
                    { _id: "3", panelName: "Government Scheme" },
                    { _id: "4", panelName: "Self Pay" },
                ]),
            });
        }
    };

    onGridReady = (params) => {
        this.setState({ gridApi: params.api, columnApi: params.columnApi });
        // Ensure Columns side panel is collapsed on load
        try {
            params.api.closeToolPanel();
        } catch (e) {
            // no-op
        }
    };

    // Filter surgeries based on multiple criteria (client-side only)
    // Date filtering is now also handled client-side
    // Text search now works in combination with other filters
    applyFilters = () => {
        const {
            surgeries,
            quickFilterText,
            doctorFilter,
            branchFilter,
            statusFilter,
            scheduledDateFilter,
        } = this.state;

        const normalizedQuery = (quickFilterText || "").trim().toLowerCase();

        let filtered = (surgeries || []).filter((surgery) => {
            // Text search filter (searches across multiple fields)
            const textMatch =
                !normalizedQuery ||
                surgery.patientName?.toLowerCase().includes(normalizedQuery) ||
                surgery.patientPhone?.toLowerCase().includes(normalizedQuery) ||
                surgery.bookingReference?.toLowerCase().includes(normalizedQuery) ||
                surgery.patientGenericPid
                    ?.toLowerCase()
                    .includes(normalizedQuery) ||
                surgery.patientId?.toLowerCase().includes(normalizedQuery) ||
                surgery.type?.toLowerCase().includes(normalizedQuery) ||
                surgery.group?.toLowerCase().includes(normalizedQuery) ||
                surgery.urgencyLevel?.toLowerCase().includes(normalizedQuery) ||
                surgery.status?.toLowerCase().includes(normalizedQuery) ||
                surgery.procedure?.toLowerCase().includes(normalizedQuery);

            // Date filter (client-side) - filter by scheduledDateFilter
            // Use local timezone for both dates to ensure consistent comparison
            const surgeryDate = moment(surgery.scheduledDate).format("YYYY-MM-DD");
            const targetDate = scheduledDateFilter
                ? moment(scheduledDateFilter).format("YYYY-MM-DD")
                : null;
            const dateMatch = !scheduledDateFilter || surgeryDate === targetDate;

            // Doctor filter (client-side)
            const doctorMatch = !doctorFilter || surgery.doctorName === doctorFilter;

            // Branch filter (client-side)
            const branchMatch = !branchFilter || surgery.branchName === branchFilter;

            // Status filter (maps user-friendly labels to values in data)
            const statusMap = {
                Confirmed: "confirmed",
                Postponed: "postponed",
                Discharged: "discharged",
                Cancelled: "cancelled",
            };
            const normalizedStatus = String(
                statusMap[statusFilter] || statusFilter || ""
            )
                .trim()
                .toLowerCase();
            const surgeryStatus = String(surgery?.status || "")
                .trim()
                .toLowerCase();
            const statusMatch =
                !normalizedStatus ||
                surgeryStatus === normalizedStatus ||
                (normalizedStatus === "postponed" &&
                    (surgeryStatus.includes("postpon") ||
                        surgeryStatus.includes("reschedul"))) ||
                (normalizedStatus === "cancelled" &&
                    surgeryStatus.includes("cancel")) ||
                (normalizedStatus === "discharged" &&
                    surgeryStatus.includes("discharg"));

            return textMatch && dateMatch && doctorMatch && branchMatch && statusMatch;
        });

        this.setState({
            filteredSurgeries: filtered,
        });
    };

    // Calculate status counts from filtered surgeries
    getStatusCounts = () => {
        const { filteredSurgeries } = this.state;
        const surgeries = Array.isArray(filteredSurgeries) ? filteredSurgeries : [];

        const counts = {
            confirmed: 0,
            postponed: 0,
            discharged: 0,
            cancelled: 0,
        };

        const resolveStatusBucket = (surgery) => {
            const rawStatus = String(
                surgery?.status ||
                surgery?.bookingStatus ||
                surgery?.workflowStatus ||
                ""
            )
                .trim()
                .toLowerCase();
            const displayLabel = String(
                this.getDisplayStatusMeta(surgery)?.label || ""
            )
                .trim()
                .toLowerCase();
            const combined = `${rawStatus} ${displayLabel}`.trim();

            if (combined.includes("cancel")) return "cancelled";

            const isRescheduledFlag = Boolean(
                surgery?.rescheduled ||
                surgery?.isRescheduled ||
                surgery?.postponed ||
                surgery?.postponementNotes
            );
            if (
                combined.includes("postpon") ||
                combined.includes("reschedul") ||
                isRescheduledFlag
            ) {
                return "postponed";
            }

            if (combined.includes("discharg")) return "discharged";

            return "confirmed";
        };

        surgeries.forEach((surgery) => {
            const bucket = resolveStatusBucket(surgery);
            counts[bucket] += 1;
        });

        return counts;
    };

    // Render status counts above the table
    renderStatusCounts = () => {
        const { filteredSurgeries } = this.state;
        const statusCounts = this.getStatusCounts();
        const totalCount = Array.isArray(filteredSurgeries) ? filteredSurgeries.length : 0;

        const confirmedMeta = this.getStatusMeta("confirmed");
        const postponedMeta = this.getStatusMeta("postponed");
        const dischargedMeta = this.getStatusMeta("discharged");
        const cancelledMeta = this.getStatusMeta("cancelled");

        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                }}
            >
                <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Surgery Bookings</H3>
                <div style={{ color: "var(--text-secondary)", display: "flex", gap: 8 }}>
                    <Tag minimal large intent="primary">
                        Total: {totalCount}
                    </Tag>
                    <Tag
                        minimal
                        large
                        style={{
                            color: confirmedMeta.color,
                            backgroundColor: confirmedMeta.background,
                            border: `1px solid ${confirmedMeta.border}`,
                        }}
                    >
                        Confirmed: {statusCounts.confirmed}
                    </Tag>
                    <Tag
                        minimal
                        large
                        style={{
                            color: postponedMeta.color,
                            backgroundColor: postponedMeta.background,
                            border: `1px solid ${postponedMeta.border}`,
                        }}
                    >
                        Postponed: {statusCounts.postponed}
                    </Tag>
                    <Tag
                        minimal
                        large
                        style={{
                            color: dischargedMeta.color,
                            backgroundColor: dischargedMeta.background,
                            border: `1px solid ${dischargedMeta.border}`,
                        }}
                    >
                        Discharged: {statusCounts.discharged}
                    </Tag>
                    <Tag
                        minimal
                        large
                        style={{
                            color: cancelledMeta.color,
                            backgroundColor: cancelledMeta.background,
                            border: `1px solid ${cancelledMeta.border}`,
                        }}
                    >
                        Cancelled: {statusCounts.cancelled}
                    </Tag>
                </div>
            </div>
        );
    };

    // Handle filter changes
    handleFilterChange = (filterType, value) => {
        this.setState({ [filterType]: value }, () => {
            this.applyFilters();
        });
    };

    // Helper function to create doctor items for searchable dropdown (shows ALL doctors)
    createDoctorItems = () => {
        // Use availableDoctors to show ALL doctors, not just those with surgeries
        return (this.state.availableDoctors || []).map((doctor) => {
            const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
            const formattedName = `${shortcode}${doctor.name}`;

            return {
                _id: doctor._id,
                name: doctor.name,
                searchName: formattedName,
                formattedName: formattedName,
            };
        });
    };

    // Helper function to get formatted doctor name from doctor object
    getFormattedDoctorFilterName = (doctor) => {
        if (!doctor) return null;
        const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
        const name = doctor.name;
        return `${shortcode}${name}`;
    };

    // Helper function to extract doctor name from formatted string
    extractDoctorNameFromFormatted = (formattedName) => {
        if (!formattedName) return null;
        // Remove shortcode part (everything before " | ")
        const withoutShortcode = formattedName.includes(" | ")
            ? formattedName.split(" | ")[1]
            : formattedName;
        return withoutShortcode.trim();
    };

    // Helper function to create branch items for searchable dropdown
    createBranchItems = () => {
        return this.state.branches.map((branch) => branch.name).filter(Boolean);
    };

    // Create payor panel items for searchable dropdown
    createPayorPanelItems = () => {
        return this.state.payorPanels.map((panel) => panel.panelName).filter(Boolean);
    };

    // Create status items for searchable dropdown
    createStatusItems = () => {
        return ["All", "Confirmed", "Postponed", "Discharged", "Cancelled"];
    };

    // Handle payor panel selection
    handlePayorPanelSelect = (selectedPanel) => {
        this.setState({ selectedPayorPanel: selectedPanel });
        // Update surgery details with the selected panel name (selectedPanel is now a string)
        this.updateSurgeryDetails("payorPanel", selectedPanel || "");

        // Auto-add an empty surgery row when payor panel is selected
        if (selectedPanel && this.state.surgeryRows.length === 0) {
            this.addSurgeryRow();
        }
    };

    // Handle status filter selection
    handleStatusFilterSelect = (statusLabel) => {
        const isAllSelected = statusLabel === "All";
        this.setState(
            {
                selectedStatusFilter: isAllSelected ? "All" : statusLabel || null,
                statusFilter: isAllSelected ? "" : statusLabel || "",
            },
            () => this.applyFilters()
        );
    };
    // Clear all filters (text, doctor, branch, status, date)
    clearAllFilters = () => {
        this.setState(
            {
                quickFilterText: "",
                doctorFilter: "",
                branchFilter: "",
                statusFilter: "",
                scheduledDateFilter: null,
                selectedDoctorFilter: null,
                selectedBranchFilter: null,
                selectedStatusFilter: "All",
            },
            () => this.applyFilters()
        );
    };

    // Create branch items for searchable dropdown
    createBranchSelectItems = () => {
        return this.state.branches.map((branch) => branch.name).filter(Boolean);
    };

    // Create doctor items for searchable dropdown
    createDoctorSelectItems = () => {
        return this.state.availableDoctors
            .map((doctor) => {
                const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
                const name = doctor.name;
                const specialization = doctor.specialization
                    ? ` (${doctor.specialization})`
                    : "";
                return `${shortcode}${name}${specialization}`;
            })
            .filter(Boolean);
    };

    // Get formatted doctor name for display
    getFormattedDoctorName = (doctor) => {
        if (!doctor) return null;
        const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
        const name = doctor.name;
        const specialization = doctor.specialization
            ? ` (${doctor.specialization})`
            : "";
        return `${shortcode}${name}${specialization}`;
    };

    // Create cash/subsidy items for searchable dropdown
    createCashSubsidyItems = () => {
        return this.state.cashSubsidyOptions
            .map((option) => option.label)
            .filter(Boolean);
    };

    // Create urgency items for searchable dropdown
    createUrgencyItems = () => {
        const urgencyOptions = [
            { value: "routine", label: "Routine" },
            { value: "urgent", label: "Urgent" },
            { value: "emergency", label: "Emergency" },
        ];
        return urgencyOptions.map((option) => option.label);
    };

    // Create surgery items for searchable dropdown
    createSurgeryItems = () => {
        const filteredSurgeries = this.getFilteredSurgeryTypes() || [];
        // Sort alphabetically to ensure consistent ordering
        const sortedSurgeries = filteredSurgeries.sort((a, b) => {
            return (a.printedName || "").localeCompare(b.printedName || "");
        });
        const items = sortedSurgeries
            .map((surgery) => surgery.printedName)
            .filter(Boolean);
        return items;
    };

    // Custom surgery item predicate with relevance sorting
    surgeryItemPredicate = (query, item, _index, exactMatch) => {
        const normalizedTitle = item.toLowerCase();
        const normalizedQuery = query.toLowerCase();

        if (exactMatch) {
            return normalizedTitle === normalizedQuery;
        } else {
            return normalizedTitle.includes(normalizedQuery);
        }
    };

    // Custom surgery item renderer
    renderSurgeryItemCustom = (item, { handleClick, modifiers, query }) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }

        return (
            <MenuItem
                active={modifiers.active}
                disabled={modifiers.disabled}
                key={item}
                onClick={handleClick}
                text={this.highlightSearchText(item, query)}
            />
        );
    };

    // Highlight search text in surgery names
    highlightSearchText = (text, query) => {
        if (!query) return text;

        const searchTerm = query.toLowerCase().trim();
        const textLower = text.toLowerCase();

        if (!textLower.includes(searchTerm)) return text;

        const index = textLower.indexOf(searchTerm);
        if (index === -1) return text;

        const before = text.slice(0, index);
        const match = text.slice(index, index + searchTerm.length);
        const after = text.slice(index + searchTerm.length);

        return (
            <span>
                {before}
                <strong style={{ backgroundColor: "rgba(19, 124, 189, 0.3)" }}>
                    {match}
                </strong>
                {after}
            </span>
        );
    };

    // Get surgery items sorted by relevance for a given query
    getSurgeryItemsSortedByRelevance = (query) => {
        const filteredSurgeries = this.getFilteredSurgeryTypes() || [];
        const sortedSurgeries = this.filterAndSortSurgeryTypes(
            query,
            filteredSurgeries
        );
        return sortedSurgeries.map((surgery) => surgery.printedName).filter(Boolean);
    };

    // Create optimized surgery items with better ordering for common search patterns
    createSurgeryItemsOptimized = () => {
        const filteredSurgeries = this.getFilteredSurgeryTypes() || [];

        // Pre-sort items to prioritize common surgery types at the top
        const priorityTerms = [
            "phaco",
            "cataract",
            "iol",
            "lasik",
            "vitrectomy",
            "retinal",
            "glaucoma",
            "corneal",
        ];

        const sortedSurgeries = filteredSurgeries.sort((a, b) => {
            const nameA = (a.printedName || "").toLowerCase();
            const nameB = (b.printedName || "").toLowerCase();

            // Check if either surgery contains priority terms
            const aPriority = priorityTerms.some((term) => nameA.includes(term));
            const bPriority = priorityTerms.some((term) => nameB.includes(term));

            if (aPriority && !bPriority) return -1;
            if (!aPriority && bPriority) return 1;

            // If both have priority or neither have priority, sort alphabetically
            return nameA.localeCompare(nameB);
        });

        const items = sortedSurgeries
            .map((surgery) => surgery.printedName)
            .filter(Boolean);
        return items;
    };

    // Custom Surgery Selector Component with relevance-based sorting
    renderSurgerySelector = (rowIndex, surgery) => {
        const { surgeryDetails, surgerySearchQueries } = this.state;

        // Get current query for this row
        const currentQuery = surgerySearchQueries[rowIndex] || "";

        // Get items sorted by relevance for current query
        const getSortedItems = (query) => {
            const filteredSurgeries = this.getFilteredSurgeryTypes() || [];
            if (!query || !query.trim()) {
                return filteredSurgeries.sort((a, b) =>
                    (a.printedName || "").localeCompare(b.printedName || "")
                );
            }
            return this.filterAndSortSurgeryTypes(query, filteredSurgeries);
        };

        return (
            <Suggest
                items={getSortedItems(currentQuery)}
                selectedItem={surgery}
                inputValueRenderer={(item) => (item ? item.printedName : "")}
                itemPredicate={(query, item) => {
                    // Since items are already filtered and sorted by getSortedItems,
                    // we just need to check if the item matches the query
                    if (!query) return true;
                    const searchTerm = query.toLowerCase().trim();
                    const surgeryName = (item.printedName || "").toLowerCase();
                    return surgeryName.includes(searchTerm);
                }}
                itemRenderer={(item, { handleClick, modifiers, query }) => {
                    if (!modifiers.matchesPredicate) return null;

                    return (
                        <MenuItem
                            key={item._id}
                            active={modifiers.active}
                            disabled={modifiers.disabled}
                            onClick={handleClick}
                            text={this.highlightSearchText(item.printedName, query)}
                        />
                    );
                }}
                onItemSelect={(selectedSurgery) => {
                    this.updateSurgeryRow(rowIndex, "surgeryId", selectedSurgery._id);
                }}
                onQueryChange={(query) => {
                    // Update query for this specific row
                    this.setState((prevState) => ({
                        surgerySearchQueries: {
                            ...prevState.surgerySearchQueries,
                            [rowIndex]: query,
                        },
                    }));
                }}
                noResults={<MenuItem disabled text="No surgeries found" />}
                popoverProps={{ minimal: true }}
                inputProps={{
                    large: true,
                    className: "v3-select",
                    style: { minWidth: "300px", width: "100%" },
                    placeholder: surgeryDetails.payorPanel
                        ? "Search surgeries..."
                        : "Select payor panel first",
                    disabled: !surgeryDetails.payorPanel,
                }}
            />
        );
    };

    // Create laterality items for searchable dropdown
    createLateralityItems = () => {
        return ["Left Eye", "Right Eye", "Both Eyes"];
    };

    // Create diet preference items for searchable dropdown
    createDietItems = () => {
        return [
            "Regular Diet",
            "Diabetic Diet",
            "Vegetarian",
            "Vegan",
            "Special Diet",
        ];
    };

    // Create bed preference items for searchable dropdown
    createBedItems = () => {
        return ["Bed", "Cabin"];
    };

    // Create anesthesia items for overall dropdown
    createAnesthesiaItems = () => {
        const items = this.state.anesthesiaOptionsForSurgery
            .map((option) => option.label)
            .filter(Boolean);
        return items;
    };

    // Handle branch selection
    handleBranchSelect = (selectedBranch) => {
        const branch = this.state.branches.find((b) => b.name === selectedBranch);
        if (branch) {
            this.onBranchSelect(branch._id);
        }
    };

    // Handle doctor selection
    handleDoctorSelect = (selectedDoctor) => {
        // Parse the formatted string to find the actual doctor
        // Format: "shortcode | name (specialization)" or "name (specialization)" or "name"
        const doctor = this.state.availableDoctors.find((d) => {
            const shortcode = d.shortcode ? `${d.shortcode} | ` : "";
            const name = d.name;
            const specialization = d.specialization ? ` (${d.specialization})` : "";
            const formattedName = `${shortcode}${name}${specialization}`;
            return formattedName === selectedDoctor;
        });
        if (doctor) {
            this.onDoctorSelect(doctor._id);
        }
    };

    // Handle advised by selection (no branch filtering; optional)
    handleAdvisedBySelect = (selectedDoctor) => {
        const doctor = this.state.availableDoctors.find((d) => {
            const shortcode = d.shortcode ? `${d.shortcode} | ` : "";
            const name = d.name;
            const specialization = d.specialization ? ` (${d.specialization})` : "";
            const formattedName = `${shortcode}${name}${specialization}`;
            return formattedName === selectedDoctor;
        });
        if (doctor) {
            this.setState({ selectedAdvisedBy: doctor });
        } else {
            this.setState({ selectedAdvisedBy: null });
        }
    };

    // Handle cash/subsidy selection
    handleCashSubsidySelect = (selectedOption) => {
        const option = this.state.cashSubsidyOptions.find(
            (o) => o.label === selectedOption
        );
        if (option) {
            this.updateSurgeryDetails("cashSubsidy", option.value);
        }
    };

    // Handle urgency selection
    handleUrgencySelect = (selectedOption) => {
        const urgencyMap = {
            Routine: "routine",
            Urgent: "urgent",
            Emergency: "emergency",
        };
        this.updateSurgeryDetails(
            "urgencyLevel",
            urgencyMap[selectedOption] || "routine"
        );
    };

    // Handle surgery selection in table
    handleSurgerySelect = (selectedSurgery, rowIndex) => {
        const filteredSurgeries = this.getFilteredSurgeryTypes() || [];
        const surgery = filteredSurgeries.find(
            (s) => s.printedName === selectedSurgery
        );
        if (surgery) {
            this.updateSurgeryRow(rowIndex, "surgeryId", surgery._id);
        }
    };

    // Handle laterality selection in table
    handleLateralitySelect = (selectedLaterality, rowIndex) => {
        const lateralityMap = {
            "Left Eye": "left",
            "Right Eye": "right",
            "Both Eyes": "both",
        };
        this.updateSurgeryRow(
            rowIndex,
            "laterality",
            lateralityMap[selectedLaterality] || ""
        );
    };

    // Handle overall anesthesia selection
    handleAnesthesiaSelect = (selectedAnesthesia) => {
        const option = this.state.anesthesiaOptionsForSurgery.find(
            (o) => o.label === selectedAnesthesia
        );
        if (option) {
            this.updateMedicalInfo("anesthesia", option.value);
        }
    };

    // Handle diet preference selection
    handleDietSelect = (selectedDiet) => {
        const dietMap = {
            "Regular Diet": "regular",
            "Diabetic Diet": "diabetic",
            Vegetarian: "vegetarian",
            Vegan: "vegan",
            "Special Diet": "special",
        };
        this.updateMedicalInfo("dietPreference", dietMap[selectedDiet] || "regular");
    };

    // Handle bed preference selection
    handleBedSelect = (selectedBed) => {
        const bedMap = {
            Bed: "Bed",
            Cabin: "Cabin",
        };
        this.updateMedicalInfo("bedPreference", bedMap[selectedBed] || "Bed");
    };

    // Handle doctor filter selection
    handleDoctorFilterSelect = (doctor) => {
        // Extract the actual doctor name from the formatted name for filtering
        const doctorName = doctor ? doctor.name : "";

        this.setState(
            {
                selectedDoctorFilter: doctor,
                doctorFilter: doctorName,
            },
            () => {
                this.applyFilters();
            }
        );
    };

    // Handle branch filter selection
    handleBranchFilterSelect = (branchName) => {
        this.setState(
            {
                selectedBranchFilter: branchName,
                branchFilter: branchName,
            },
            () => {
                this.applyFilters();
            }
        );
    };

    // Navigate to surgery details page
    navigateToDetails = (surgery) => {
        const detailsPath = `/v3/workflow/surgery-booking/${surgery.id || surgery.bookingReference
            }`;
        this.props.history.push(detailsPath);
    };

    // Status rendering methods (centralized)
    toTitleCase = (value) => toTitleCaseGlobal(value);
    getStatusMeta = (status) => getStatusMetaGlobal(status);
    getDisplayStatusMeta = (row) => {
        // Mirror MasterReport: rely solely on API-provided status/isBilled without local overrides
        const status = row?.status || "-";
        const payorPanel = row?.payorPanel || row?.payor || row?.patientPanel || "";
        const isBilled = row?.isBilled ?? false;
        return getDisplayStatusMetaGlobal({ status, payorPanel, isBilled });
    };

    renderStatusCell = (row) => {
        if (!row) return "-";
        const meta = this.getDisplayStatusMeta(row);
        return `<span style="display:inline-flex;align-items:center;justify-content:center;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;color:${meta.color};background:${meta.background};border:1px solid ${meta.border};text-transform:none;line-height:1;">${meta.label}</span>`;
    };

    columnDefs = [
        {
            headerName: "Booking Ref",
            field: "bookingReference",
            width: 150,
            filter: "agTextColumnFilter",
            pinned: "left",
            cellRenderer: (params) => {
                const container = document.createElement("div");
                const link = document.createElement("span");

                link.innerHTML = params.value || "";
                link.style.color = "#137cbd";
                link.style.cursor = "pointer";
                link.style.textDecoration = "underline";
                link.style.fontWeight = "600";
                link.onclick = () => this.navigateToDetails(params.data);

                container.appendChild(link);
                return container;
            },
        },
        {
            headerName: "Status",
            field: "status",
            width: 120,
            filter: "agSetColumnFilter",
            pinned: "left",
            filterValueGetter: (params) => this.getDisplayStatusMeta(params.data || {}).label,
            valueFormatter: (params) => this.getDisplayStatusMeta(params.data || {}).label,
            cellRenderer: (params) => this.renderStatusCell(params.data),
            cellStyle: { textAlign: "center" },
        },
        {
            headerName: "Is Billed",
            field: "isBilled",
            width: 120,
            filter: "agSetColumnFilter",
            pinned: "left",
            valueGetter: (params) => getIsBilledLabel(params.data),
            valueFormatter: (params) => getIsBilledLabel(params.data),
            cellRenderer: (params) => getIsBilledPill(params.data),
            cellStyle: { textAlign: "center" },
        },
        {
            headerName: "PID",
            field: "patientGenericPid",
            width: 160,
            filter: "agTextColumnFilter",
            pinned: "left",
        },
        {
            headerName: "Patient Name",
            field: "patientName",
            width: 180,
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Phone",
            field: "patientPhone",
            width: 140,
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Surgery Name",
            field: "type",
            width: 200,
            filter: "agTextColumnFilter",
            valueGetter: (params) => {
                const surgeries = params.data?.selectedSurgeries || [];
                if (!Array.isArray(surgeries) || surgeries.length === 0) return "";
                return surgeries
                    .map((s) => s?.name || s?.printedName || "")
                    .filter(Boolean)
                    .join(", ");
            },
            cellRenderer: (params) => {
                const surgeries = params.data?.selectedSurgeries || [];
                if (surgeries.length === 0) return "";
                const printedNames = surgeries.map((s) => s.name || s.printedName || "Unknown").join(", ");
                return printedNames;
            },
        },
        {
            headerName: "Group",
            field: "group",
            width: 150,
            filter: "agTextColumnFilter",
            valueGetter: (params) => {
                const surgeries = params.data?.selectedSurgeries || [];
                if (!Array.isArray(surgeries) || surgeries.length === 0) return params.data?.group || "";
                const groups = [...new Set(surgeries.map((s) => s?.group).filter(Boolean))];
                return groups.join(", ");
            },
            cellRenderer: (params) => {
                const surgeries = params.data?.selectedSurgeries || [];
                if (surgeries.length === 0) return "";
                const groups = [...new Set(surgeries.map((s) => s.group).filter(Boolean))];
                return groups.join(", ");
            },
        },
        {
            headerName: "Eye",
            field: "eye",
            width: 100,
            filter: "agSetColumnFilter",
            valueGetter: (params) => {
                const eye = params.data?.selectedSurgeries?.[0]?.laterality || params.data?.eye || "";
                return eye || "";
            },
            valueFormatter: (params) => {
                const val = params.value || "";
                return val ? _.startCase(val) : "";
            },
            filterParams: {
                values: ["left", "right", "both"],
            },
        },
        {
            headerName: "Additional Notes",
            field: "additionalNotes",
            width: 200,
            filter: "agTextColumnFilter",
            cellRenderer: (params) => {
                const notes = params.value || "";
                return notes.length > 50 ? `${notes.substring(0, 50)}...` : notes;
            },
        },
        {
            headerName: "Doctor",
            field: "doctorName",
            width: 150,
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Branch",
            field: "branchName",
            width: 150,
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Date",
            field: "scheduledDate",
            width: 120,
            filter: "agDateColumnFilter",
            valueGetter: (params) => {
                const v = params.data?.scheduledDate;
                if (!v) return null;
                try {
                    return new Date(v);
                } catch (e) {
                    return null;
                }
            },
            valueFormatter: (params) => (params.value ? moment(params.value).format("DD/MM/YYYY") : ""),
        },
        {
            headerName: "Time",
            field: "scheduledTime",
            width: 100,
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Time Frame",
            field: "timeFrame",
            width: 140,
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Booked by",
            field: "createdByName",
            width: 160,
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Urgency",
            field: "urgencyLevel",
            width: 120,
            filter: "agSetColumnFilter",
            filterParams: {
                values: ["urgent", "asap", "routine", "elective"],
            },
            cellRenderer: (params) => {
                const urgency = params.value;
                const color = urgency === "urgent" ? "#db3737" : urgency === "asap" ? "#f29d49" : "#5cb85c";
                return `<span style="color: ${color}; font-weight: bold;">${_.startCase(urgency)}</span>`;
            },
        },
    ];

    // Patient Search Functions
    searchPatients = async (string) => {
        if (!string) {
            this.setState({ oldSearchResults: [] });
            return;
        }
        try {
            this.setState({ oldSearching: true });
            let res = await apiCallPost(`/rai/${this.props.authStore.prefix}/search`, {
                prefix: string,
            });
            this.setState({
                oldSearchResults: res.map((each) => {
                    return {
                        item: _.startCase(_.lowerCase(each.name)),
                        entityTypeString: each.genericPid,
                        helper: each.phone,
                        helper2: new Date().getFullYear() - new Date(each.dob).getFullYear(),
                        patientData: each,
                    };
                }),
                oldSearching: false,
            });
        } catch (err) {
            cogoToast.error(
                "There was an error in searching. Please check the console for more details."
            );
            console.error(err);
            this.setState({ oldSearching: false });
        }
    };

    // Returns empty string for addresses that are just country/state defaults with no real value
    sanitizeAddress = (addr) => {
        if (!addr || typeof addr !== "string") return "";
        const trimmed = addr.trim();
        const JUNK_DEFAULTS = ["india", "in", "-", "na", "n/a", "none", "null", "undefined"];
        if (JUNK_DEFAULTS.includes(trimmed.toLowerCase())) return "";
        return trimmed;
    };

    selectPatient = async (item) => {
        let obj;
        try {
            let res = await apiCallPost(
                `/clinical2/${this.props.authStore.prefix}/extra/getPatientInfo`,
                {
                    patientId: item.patientData.genericPid,
                }
            );

            // Normalize sex to standard codes 'M' or 'F' for consistent UI display
            const sexStr =
                typeof res.sex === "string" ? res.sex.trim().toLowerCase() : "";
            const sexCode =
                sexStr === "m" || sexStr === "male"
                    ? "M"
                    : sexStr === "f" || sexStr === "female"
                        ? "F"
                        : "";

            // Address: use patient profile first, fall back to latest surgery's stored address
            const resolvedAddress =
                this.sanitizeAddress(res.patientAddress) ||
                this.sanitizeAddress(res.latestSurgery?.patientAddress);

            obj = {
                _id: res.sysPid,
                name: res.name,
                phone: res.phone,
                patientAddress: resolvedAddress,
                sex: sexCode,
                sexLabel: typeof res.sex === "string" ? res.sex : "",
                age: this.calculateAgeFromDob(res.dob),
                originalData: res,
            };
        } catch (err) {
            cogoToast.error(
                "There was an error in searching. Please check the console for more details."
            );
            console.error(err);
            return;
        }

        this.setState(
            {
                selectedPatient: obj,
                whatsappNumber: obj.phone || "",
                patientName: obj.name || "",
                patientPhone: obj.phone || "",
                patientAddress: obj.patientAddress || "",
                patientDob: (() => {
                    try {
                        const raw = obj?.originalData?.dob;
                        if (!raw) return null;
                        const dt = moment(raw).toDate();
                        return dt && !isNaN(dt.getTime()) ? dt : null;
                    } catch (e) {
                        return null;
                    }
                })(),
                patientSex: obj.sex || "",
                oldSearchResults: [],
                patientSearchQuery: obj.name,
            },
            () => {
                // Focus on address field after patient is selected
                setTimeout(() => {
                    if (this.patientAddressRef.current) {
                        this.patientAddressRef.current.focus();
                    }
                }, 100);
            }
        );
    };

    // Calculate accurate age in years from DOB
    calculateAgeFromDob = (dob) => {
        if (!dob) return null;
        const birth = new Date(dob);
        if (isNaN(birth.getTime())) return null;
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age -= 1;
        }
        return age;
    };

    // Doctor & Branch Functions
    onBranchSelect = async (branchId) => {
        const branch = this.state.branches.find((b) => b._id === branchId);
        this.setState({
            selectedBranch: branch,
            selectedDoctor: null, // Reset doctor selection when branch changes
            selectedDate: null, // Reset calendar selections
            selectedTimeSlot: null,
            chosenTime: "",
        });

        // Only load doctors if not already loaded
        if ((this.state.availableDoctors || []).length === 0) {
            await this.loadAvailableDoctors(branchId);
        }

        // Focus on doctor field after a brief delay to allow UI update
        setTimeout(() => {
            const doctorInput = document.querySelector(
                '[placeholder*="Search doctors"]'
            );
            if (doctorInput) {
                doctorInput.focus();
            }
        }, 100);

    };

    loadAvailableDoctors = async (branchId) => {
        if ((this.state.availableDoctors || []).length > 0) {
            return;
        }
        if (this._doctorsPromise) {
            return this._doctorsPromise;
        }
        this.setState({ isLoadingDoctorsList: true });
        this._doctorsPromise = (async () => {
            try {
                const cachedConsultants = window.__neoCache?.consultants;
                if (Array.isArray(cachedConsultants) && cachedConsultants.length > 0) {
                    if (this._isMounted) {
                        this.setState({ availableDoctors: cachedConsultants });
                    }
                    return;
                }
                if (window.__neoInflight?.getAllConsultants) {
                    const resp = await window.__neoInflight.getAllConsultants;
                    if (Array.isArray(resp) && resp.length > 0) {
                        if (this._isMounted) {
                            this.setState({ availableDoctors: resp });
                        }
                        if (typeof window !== "undefined") {
                            window.__neoCache.consultants = resp;
                        }
                        return;
                    }
                }
                window.__neoInflight.getAllConsultants = apiCallPost(
                    `/rai/${this.props.authStore.prefix}/getAllConsultants`,
                    {}
                );
                const response = await window.__neoInflight.getAllConsultants;
                if (this._isMounted) {
                    this.setState({ availableDoctors: response || [] });
                }
                if (typeof window !== "undefined") {
                    window.__neoCache.consultants = response || [];
                }
            } catch (error) {
                console.error("Error loading doctors:", error);
                cogoToast.error("Failed to load doctors");
                if (this._isMounted) {
                    this.setState({
                        availableDoctors: [
                            { _id: "1", name: "Dr. Ammar", shortcode: "AMM" },
                            { _id: "2", name: "Dr. Smith", shortcode: "SMT" },
                            { _id: "3", name: "Dr. Johnson", shortcode: "JOH" },
                            { _id: "4", name: "Dr. Williams", shortcode: "WIL" },
                        ],
                    });
                }
            } finally {
                if (this._isMounted) {
                    this.setState({ isLoadingDoctorsList: false });
                }
                this._doctorsPromise = null;
            }
        })();
        return this._doctorsPromise;
    };

    onDoctorSelect = async (doctorId) => {
        const doctor = this.state.availableDoctors.find((d) => d._id === doctorId);
        this.setState({ selectedDoctor: doctor });
    };

    // Calendar & Scheduling Functions
    onDateSelect = (date) => {
        this.setState({
            selectedDate: date,
            selectedTime: "",
            selectedTimeSlot: null,
        });
    };

    onTimeSlotSelect = (timeSlot) => {
        this.setState({
            selectedTime: timeSlot.time,
            selectedTimeSlot: timeSlot,
            chosenTime: moment(timeSlot.time, "hh:mm A").format("HH:mm"),
        });
    };

    // Load booked counts for time slots
    loadBookedCounts = async (doctorId, branchId, date, timeFrame) => {
        try {
            const dateString = moment(date).format("YYYY-MM-DD");

            const response = await apiCallPost(
                `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`,
                {
                    doctorId: doctorId,
                    branchName: this.state.selectedBranch?.name || branchId,
                    startDate: dateString,
                    endDate: dateString,
                    page: 1,
                    limit: 100, // Should be enough for a single day
                    mode: "calendar",
                }
            );

            // Handle the API response structure properly
            const surgeriesRaw = response?.data?.items
                ? response.data.items
                : response || [];
            const surgeries = filterBookingsForSlotMetrics(surgeriesRaw);

            // Filter by specific time frame if provided
            const filteredSurgeries = timeFrame
                ? surgeries.filter((surgery) => surgery.timeFrame === timeFrame)
                : surgeries;

            return filteredSurgeries.length;
        } catch (error) {
            console.error("Error loading booked counts:", error);
            return 0;
        }
    };

    // Validation Functions
    validateForm = (options = {}) => {
        const { silent = false } = options;
        const {
            selectedPatient,
            selectedBranch,
            selectedDoctor,
            selectedDate,
            selectedTimeSlot,
            chosenTime,
        } = this.state;
        const showError = (message) => {
            if (!silent) {
                cogoToast.error(message);
            }
        };

        if (!selectedPatient) {
            showError("Please select a patient");
            return false;
        }
        if (!this.hasAnySurgerySelected()) {
            showError("Please select at least one surgery");
            return false;
        }
        if (this.hasIncompleteSurgeryRows()) {
            showError("Please fill surgery and laterality for every entry or remove unused rows");
            return false;
        }
        // Require overall anesthesia selection
        if (!this.state.medicalInfo || !this.state.medicalInfo.anesthesia) {
            showError("Please select anesthesia type");
            return false;
        }
        if (!this.state.selectedAdvisedBy) {
            showError("Please select an advising doctor");
            return false;
        }
        if (!selectedBranch || !selectedDoctor) {
            showError("Please select branch and doctor");
            return false;
        }
        if (!selectedDate || !selectedTimeSlot) {
            showError("Please select date and time slot");
            return false;
        }

        // Validate chosen time is within selected slot
        if (chosenTime && selectedTimeSlot?.timeFrame) {
            const [startStr, endStr] = selectedTimeSlot.timeFrame.split(" - ");
            if (startStr && endStr) {
                const chosenMoment = moment(chosenTime, "HH:mm");
                const startMoment = moment(startStr, "hh:mm A");
                const endMoment = moment(endStr, "hh:mm A");

                if (chosenMoment.isBefore(startMoment) || chosenMoment.isAfter(endMoment)) {
                    showError(`Please select a time between ${startStr} and ${endStr} for the chosen slot`);
                    return false;
                }
            }
        }

        return true;
    };

    // Get validation errors for tooltip display
    getValidationErrors = () => {
        const {
            selectedPatient,
            selectedBranch,
            selectedDoctor,
            selectedDate,
            selectedTimeSlot,
            chosenTime,
            medicalInfo,
        } = this.state;
        const errors = [];

        if (!selectedPatient) {
            errors.push("Select a patient");
        }
        if (!selectedBranch || !selectedDoctor) {
            errors.push("Select branch and doctor");
        }
        if (!selectedDate || !selectedTimeSlot) {
            errors.push("Select date and time slot");
        }
        if (!this.hasAnySurgerySelected()) {
            errors.push("Select at least one surgery");
        } else if (this.hasIncompleteSurgeryRows()) {
            errors.push("Complete surgery and laterality for every entry or remove unused rows");
        }
        if (!medicalInfo || !medicalInfo.anesthesia) {
            errors.push("Select anesthesia type");
        }
        if (!this.state.selectedAdvisedBy) {
            errors.push("Select an advising doctor");
        }

        // Validate chosen time is within selected slot
        if (chosenTime && selectedTimeSlot?.timeFrame) {
            const [startStr, endStr] = selectedTimeSlot.timeFrame.split(" - ");
            if (startStr && endStr) {
                const chosenMoment = moment(chosenTime, "HH:mm");
                const startMoment = moment(startStr, "hh:mm A");
                const endMoment = moment(endStr, "hh:mm A");

                if (chosenMoment.isBefore(startMoment) || chosenMoment.isAfter(endMoment)) {
                    errors.push(`Chosen time must be between ${startStr} and ${endStr}`);
                }
            }
        }

        return errors;
    };

    // Surgery CRUD Functions
    editSurgery = (surgery) => {
        // Validate surgery data
        if (!surgery || !surgery.id) {
            cogoToast.error("Invalid surgery data for editing");
            return;
        }

        cogoToast.loading("Loading surgery details for editing...");

        // Set up the edit form with current surgery data
        this.setState(
            {
                showEditForm: true,
                editingSurgery: surgery,
                // Pre-populate form with existing data
                selectedPatient: {
                    _id: surgery.patientId,
                    name: surgery.patientName,
                    phone: surgery.patientPhone,
                    age: surgery.patientAge || 0,
                    sex: surgery.patientSex || "M",
                    originalData: surgery.patientOriginalData || {},
                },
                // Pre-populate editable patient fields
                patientName: (surgery.patientName || "").trim(),
                patientPhone: (surgery.patientPhone || "").trim(),
                patientAddress: surgery.patientAddress || "",
                patientDob: (() => {
                    try {
                        const d = surgery.patientDob || surgery.patientOriginalData?.dob;
                        const dv = d ? moment(d).toDate() : null;
                        return dv && !isNaN(dv.getTime()) ? dv : null;
                    } catch (e) {
                        return null;
                    }
                })(),
                patientSex: (() => {
                    const s = surgery.patientSex || "";
                    const up = String(s).toUpperCase();
                    if (up === "M" || up === "F" || up === "O") return up;
                    return "";
                })(),
                surgeryDetails: {
                    group: surgery.group || "",
                    eye: surgery.eye || "",
                    procedure: surgery.procedure || "",
                    urgencyLevel: surgery.urgencyLevel || "routine",
                    estimatedDuration: surgery.estimatedDuration || 60,
                    selectedSurgeries: surgery.selectedSurgeries || [], // Expect combined array from API
                    payorPanel: surgery.payorPanel || "",
                    cashSubsidy: surgery.cashSubsidy || "Cash",
                },
                // Set selectedPayorPanel for the searchable dropdown (now a string)
                selectedPayorPanel: surgery.payorPanel || null,
                selectedSurgeriesFromAPI: surgery.selectedSurgeries
                    ? surgery.selectedSurgeries.map((surgeryItem) => ({
                        _id: surgeryItem.id,
                        printedName: surgeryItem.name,
                        payor: surgeryItem.payor || "",
                    }))
                    : [],
                // Populate surgeryRows for the table with existing data (anesthesia now at overall level)
                surgeryRows: surgery.selectedSurgeries
                    ? surgery.selectedSurgeries.map((surgeryItem, idx) => {
                        return {
                            id: surgeryItem.id,
                            name: surgeryItem.name,
                            laterality: surgeryItem.laterality || "",
                            estimatedCost: surgeryItem.estimatedCost || 0,
                        };
                    })
                    : [],
                selectedBranch: surgery.branchId
                    ? { _id: surgery.branchId, name: surgery.branchName }
                    : null,
                selectedDoctor: surgery.doctorId
                    ? { _id: surgery.doctorId, name: surgery.doctorName }
                    : null,
                selectedDate: surgery.scheduledDate
                    ? moment(surgery.scheduledDate).toDate()
                    : null,
                selectedTime: surgery.scheduledTime || "",
                medicalInfo: {
                    dietPreference: surgery.dietPreference || "regular",
                    bedPreference: surgery.bedPreference || "Bed",
                    isDiabetic: surgery.isDiabetic || false,
                    additionalNotes: surgery.additionalNotes || "",
                    allergies: surgery.allergies || "",
                    currentMedications: surgery.currentMedications || "",
                    anesthesia: surgery.anesthesia || "",
                },
                whatsappNumber: surgery.whatsappNumber || surgery.patientPhone || "",
                enableWhatsappConfirmation: surgery.enableWhatsappConfirmation !== false,
                enableEmailConfirmation: surgery.enableEmailConfirmation !== false,
            },
            () => {
                // Load available doctors if branch is selected
                if (surgery.branchId) {
                    this.loadAvailableDoctors(surgery.branchId);
                }
                // Ensure anesthesia options are loaded for editing
                if ((this.state.anesthesiaOptionsForSurgery || []).length === 0) {
                    this.loadAnesthesiaOptionsForSurgery();
                }
                this.ensureMasterDropdownsReady();
                cogoToast.success("Surgery details loaded for editing");
            }
        );
    };

    hideEditForm = () => {
        this.setState({
            showEditForm: false,
            editingSurgery: null,
        });
        this.resetBooking();
    };

    updateSurgery = async () => {
        if (!this.validateForm()) {
            return;
        }

        this.setState({ isUpdating: true });

        try {
            const procedureDischargeSummaryName =
                this.getProcedureDischargeSummaryName();
            const updateData = {
                id: this.state.editingSurgery.id,
                patientId: this.state.selectedPatient._id,
                patientGenericPid: this.state.selectedPatient._id,
                patientName: (
                    this.state.patientName ||
                    this.state.selectedPatient.name ||
                    ""
                ).trim(),
                patientPhone: (
                    this.state.patientPhone ||
                    this.state.selectedPatient.phone ||
                    ""
                ).trim(),
                patientOriginalData: this.state.selectedPatient.originalData,
                patientDob: this.state.patientDob
                    ? moment(this.state.patientDob).toISOString()
                    : this.state.selectedPatient?.originalData?.dob || null,
                patientSex: this.state.patientSex || this.state.selectedPatient.sex || "",
                type: (this.state.surgeryRows || []).map((r) => r.id).filter(Boolean),
                selectedSurgeries: (this.state.surgeryRows || []).map((r) => {
                    // Find the surgery in printedNames to get its group
                    const surgery = this.state.printedNames.find((s) => s._id === r.id);
                    return {
                        id: r.id,
                        name: r.name,
                        laterality: r.laterality,
                        estimatedCost: r.estimatedCost,
                        group: surgery?.group || "",
                    };
                }),
                group: this.state.surgeryDetails.group || "",
                surgeryGroup: this.getSurgeryGroups().join(", ") || "",
                // eye removed; using per-row laterality
                procedure: this.state.surgeryDetails.procedure || "",
                payorPanel: this.state.surgeryDetails.payorPanel || "",
                cashSubsidy: this.state.surgeryDetails.cashSubsidy,
                urgencyLevel: this.state.surgeryDetails.urgencyLevel,
                estimatedDuration: this.state.surgeryDetails.estimatedDuration,
                doctorId: this.state.selectedDoctor._id,
                doctorName: this.state.selectedDoctor.name,
                branchId: this.state.selectedBranch._id,
                branchName: this.state.selectedBranch.name,
                scheduledDate: this.state.selectedDate
                    ? moment(this.state.selectedDate).toISOString()
                    : null,
                scheduledTime: this.state.selectedTime,
                timeFrame: this.state.selectedTimeSlot
                    ? this.state.selectedTimeSlot.timeFrame
                    : "",
                slotCapacity: this.state.doctorCapacity?.slotCapacity || 0,
                slotBookedCount: this.state.doctorCapacity?.slotBookedCount || 0,
                dietPreference: this.state.medicalInfo.dietPreference,
                bedPreference: this.state.medicalInfo.bedPreference,
                isDiabetic: this.state.medicalInfo.isDiabetic,
                additionalNotes: this.state.medicalInfo.additionalNotes,
                allergies: this.state.medicalInfo.allergies,
                currentMedications: this.state.medicalInfo.currentMedications,
                anesthesia: this.state.medicalInfo.anesthesia,
                whatsappNumber:
                    this.state.whatsappNumber ||
                    this.state.patientPhone ||
                    this.state.selectedPatient.phone ||
                    "",
                enableWhatsappConfirmation: this.state.enableWhatsappConfirmation,
                enableEmailConfirmation: this.state.enableEmailConfirmation,
                status: this.state.editingSurgery.status, // Keep existing status
                updatedAt: new Date().toISOString(),
                consolidatedBilling: this.buildConsolidatedBillingSnapshot(),
                procedureDischargeSummaryName,
            };

            cogoToast.loading("Updating surgery booking...");

            const response = await apiCallPost(
                `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update`,
                updateData
            );

            // Update local state
            this.setState((prevState) => ({
                surgeries: prevState.surgeries.map((s) =>
                    s.id === this.state.editingSurgery.id ? { ...s, ...updateData } : s
                ),
                filteredSurgeries: prevState.filteredSurgeries.map((s) =>
                    s.id === this.state.editingSurgery.id ? { ...s, ...updateData } : s
                ),
                isUpdating: false,
                showEditForm: false,
                editingSurgery: null,
            }));

            cogoToast.success("Surgery booking updated successfully!");
        } catch (err) {
            cogoToast.error("Failed to update surgery booking");
            console.error("Failed to update surgery booking:", err);
            this.setState({ isUpdating: false });
        }
    };

    deleteSurgery = async (surgeryId) => {
        if (
            !window.confirm("Are you sure you want to delete this surgery booking?")
        ) {
            return;
        }

        try {
            cogoToast.loading("Deleting surgery booking...");
            await apiCallPost(
                `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/delete`,
                { id: surgeryId }
            );

            // Remove from local state
            this.setState((prevState) => ({
                surgeries: prevState.surgeries.filter((s) => s.id !== surgeryId),
                filteredSurgeries: prevState.filteredSurgeries.filter(
                    (s) => s.id !== surgeryId
                ),
            }));

            cogoToast.success("Surgery booking deleted successfully");
        } catch (err) {
            cogoToast.error("Failed to delete surgery booking");
            console.error("Detailed error:", err);
        }
    };

    // Booking Functions
    bookSurgery = async () => {
        // Enhanced validation with detailed logging
        const validationResult = this.validateForm();

        if (!validationResult) {
            return;
        }

        this.setState({ isBooking: true });

        try {
            const procedureDischargeSummaryName =
                this.getProcedureDischargeSummaryName();
            const bookingData = {
                patientId: this.state.selectedPatient._id,
                patientGenericPid: this.state.selectedPatient._id,
                patientName: (
                    this.state.patientName ||
                    this.state.selectedPatient.name ||
                    ""
                ).trim(),
                patientPhone: (
                    this.state.patientPhone ||
                    this.state.selectedPatient.phone ||
                    ""
                ).trim(),
                patientAddress: this.state.patientAddress || "",
                patientDob: this.state.patientDob
                    ? moment(this.state.patientDob).toISOString()
                    : this.state.selectedPatient?.originalData?.dob || null,
                patientSex: this.state.patientSex || this.state.selectedPatient.sex || "",
                patientOriginalData: this.state.selectedPatient.originalData,
                type: (this.state.surgeryRows || []).map((r) => r.id).filter(Boolean),
                selectedSurgeries: (this.state.surgeryRows || []).map((r) => {
                    // Find the surgery in printedNames to get its group
                    const surgery = this.state.printedNames.find((s) => s._id === r.id);
                    return {
                        id: r.id,
                        name: r.name,
                        laterality: r.laterality,
                        estimatedCost: r.estimatedCost,
                        group: surgery?.group || "",
                    };
                }),
                group: this.state.surgeryDetails.group || "",
                surgeryGroup: this.getSurgeryGroups().join(", ") || "",
                procedure: this.state.surgeryDetails.procedure || "",
                payorPanel: this.state.surgeryDetails.payorPanel || "",
                cashSubsidy: this.state.surgeryDetails.cashSubsidy,
                urgencyLevel: this.state.surgeryDetails.urgencyLevel,
                estimatedDuration: this.state.surgeryDetails.estimatedDuration,
                doctorId: this.state.selectedDoctor._id,
                doctorName: this.state.selectedDoctor.name,
                branchId: this.state.selectedBranch._id,
                branchName: this.state.selectedBranch.name,
                scheduledDate: this.state.selectedDate,
                scheduledTime: this.state.chosenTime
                    ? moment(this.state.chosenTime, "HH:mm").format("hh:mm A")
                    : this.state.selectedTimeSlot.time,
                originalScheduledDate: this.state.selectedDate,
                originalScheduledTime: this.state.chosenTime
                    ? moment(this.state.chosenTime, "HH:mm").format("hh:mm A")
                    : this.state.selectedTimeSlot.time,
                timeFrame: this.state.selectedTimeSlot.timeFrame,
                slotCapacity: this.state.selectedTimeSlot.capacity,
                slotBookedCount: this.state.selectedTimeSlot.bookedCount,
                dietPreference: this.state.medicalInfo.dietPreference,
                bedPreference: this.state.medicalInfo.bedPreference,
                isDiabetic: this.state.medicalInfo.isDiabetic,
                additionalNotes: this.state.medicalInfo.additionalNotes,
                allergies: this.state.medicalInfo.allergies,
                currentMedications: this.state.medicalInfo.currentMedications,
                anesthesia: this.state.medicalInfo.anesthesia,
                whatsappNumber:
                    this.state.whatsappNumber ||
                    this.state.patientPhone ||
                    this.state.selectedPatient.phone ||
                    "",
                enableWhatsappConfirmation: this.state.enableWhatsappConfirmation,
                enableEmailConfirmation: this.state.enableEmailConfirmation,
                advisedBy: this.state.selectedAdvisedBy?.name || "",
                advisedById: this.state.selectedAdvisedBy?._id || "",
                status: "confirmed",
                createdById: this.props.authStore.user?._id || "system",
                createdByName: this.props.authStore.user?.name || "System",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                consolidatedBilling: this.buildConsolidatedBillingSnapshot(),
                rescheduled: false,
                procedureDischargeSummaryName,
            };
            const response = await apiCallPost(
                `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/create`,
                bookingData
            );

            cogoToast.success("Surgery booked successfully!");

            const responseBookingReference =
                response?.data?.bookingReference || response?.bookingReference || "";
            this.setState({ bookingReference: responseBookingReference });

            const createdId =
                (response && (response.data?._id || response.data?.id)) ||
                response?._id ||
                response?.id;

            // Dedicated audit row for payor panel at creation (visible in booking Audit tab)
            if (createdId && this.props.authStore?.prefix) {
                const payorPanelVal = (bookingData.payorPanel || "").trim();
                const resourceName = `Surgery - ${(
                    bookingData.patientName ||
                    "Unknown Patient"
                ).trim()}`;
                try {
                    await apiCallPost(
                        `/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`,
                        [
                            {
                                resourceType: "SURGERY_BOOKING",
                                resourceId: createdId,
                                resourceName,
                                actionType: "CREATE",
                                actionSlug: `Payor panel assigned at booking creation: "${payorPanelVal || "None"}"`,
                                actionMetadata: {
                                    fieldName: "payorPanel",
                                    changeType: "CREATE",
                                    previousValue: "",
                                    newValue: payorPanelVal,
                                },
                            },
                        ]
                    );
                } catch (auditErr) {
                    console.error(
                        "Audit log failed for payor panel at booking creation:",
                        auditErr
                    );
                }
            }

            // Redirect directly to SX Booking Details page if ID is available
            try {
                if (createdId) {
                    this.props.history.push(`/v3/workflow/surgery-booking/${createdId}`);
                    return;
                }
            } catch (e) {
                // Non-blocking; fallback to existing flow
            }

            // Send confirmations if enabled
            if (this.state.enableWhatsappConfirmation) {
                this.sendWhatsAppConfirmation({
                    ...bookingData,
                    bookingReference: responseBookingReference,
                });
            }

            // Refresh the surgery list to show the newly created surgery
            await this.loadSurgeries();

            // Reset only the form fields (not the surgery list) to go back to surgery list view
            this.resetFormFields();
        } catch (error) {
            console.error("❌ Booking error details:", {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                statusText: error.response?.statusText,
                config: {
                    url: error.config?.url,
                    method: error.config?.method,
                    data: error.config?.data ? "Present" : "Missing",
                },
                fullError: error,
            });

            // More specific error messages based on error type
            let errorMessage = "Failed to book surgery. Please try again.";

            if (error.response?.status === 401) {
                errorMessage = "Authentication failed. Please login again.";
            } else if (error.response?.status === 403) {
                errorMessage = "You don't have permission to book surgeries.";
            } else if (error.response?.status === 404) {
                errorMessage = "Booking service not found. Please contact support.";
            } else if (error.response?.status >= 500) {
                errorMessage = "Server error occurred. Please try again later.";
            } else if (error.message?.includes("Network Error")) {
                errorMessage =
                    "Network connection failed. Please check your internet connection.";
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            cogoToast.error(errorMessage);
            this.setState({ isBooking: false });
        }
    };

    // Get surgery groups from selected surgeries
    getSurgeryGroups = () => {
        if (!this.state.surgeryRows || this.state.surgeryRows.length === 0) {
            return [];
        }

        // Get unique groups from selected surgeries using the 'group' field from printedNames
        const groups = new Set();

        this.state.surgeryRows.forEach((row) => {
            if (row.id) {
                // Find the surgery in printedNames to get its group
                const surgery = this.state.printedNames.find((s) => s._id === row.id);
                if (surgery && surgery.group) {
                    groups.add(surgery.group);
                }
            }
        });

        return Array.from(groups).filter(Boolean);
    };

    getProcedureDischargeSummaryName = (rowsOverride = null) => {
        const rows = Array.isArray(rowsOverride)
            ? rowsOverride
            : this.state.surgeryRows || [];
        return buildProcedureDischargeSummaryName(rows);
    };

    sendWhatsAppConfirmation = async (bookingData) => {
        try {
            // Mock WhatsApp API call
            cogoToast.success("WhatsApp confirmation sent!");
        } catch (error) {
            console.error("WhatsApp error:", error);
        }
    };

    resetBooking = () => {
        this.setState({
            selectedPatient: null,
            selectedPayorPanel: null,
            patientSearchQuery: "",
            patientAddress: "",
            patientName: "",
            patientPhone: "",
            patientDob: null,
            patientSex: "",
            searchResults: [],
            oldSearchResults: [],
            surgeryDetails: {
                group: "",
                eye: "",
                procedure: "",
                urgencyLevel: "routine",
                estimatedDuration: 60,
                selectedSurgeries: [],
                payorPanel: "",
                cashSubsidy: "Cash",
            },
            selectedBranch: null,
            selectedDoctor: null,
            selectedAdvisedBy: null,
            selectedDate: null,
            selectedTime: "",
            selectedTimeSlot: null,
            chosenTime: "",
            selectedSurgeriesFromAPI: [],
            medicalInfo: {
                dietPreference: "regular",
                bedPreference: "Bed",
                isDiabetic: false,
                additionalNotes: "",
                allergies: "",
                currentMedications: "",
                anesthesia: "",
            },
            showConfirmation: false,
            bookingReference: "",
            showCreateForm: false,
        });
    };

    resetFormFields = () => {
        this.setState({
            selectedPatient: null,
            selectedPayorPanel: null,
            patientSearchQuery: "",
            patientAddress: "",
            patientName: "",
            patientPhone: "",
            patientDob: null,
            patientSex: "",
            searchResults: [],
            oldSearchResults: [],
            surgeryDetails: {
                group: "",
                eye: "",
                procedure: "",
                urgencyLevel: "routine",
                estimatedDuration: 60,
                selectedSurgeries: [],
                payorPanel: "",
                cashSubsidy: "Cash",
            },
            selectedBranch: null,
            selectedDoctor: null,
            selectedAdvisedBy: null,
            selectedDate: null,
            selectedTime: "",
            selectedTimeSlot: null,
            chosenTime: "",
            selectedSurgeriesFromAPI: [],
            medicalInfo: {
                dietPreference: "regular",
                bedPreference: "Bed",
                isDiabetic: false,
                additionalNotes: "",
                allergies: "",
                currentMedications: "",
                anesthesia: "",
            },
            showConfirmation: false,
            bookingReference: "",
            showCreateForm: false,
            isBooking: false,
        });
    };

    showCreateForm = () => {
        this.openCreateForm();
    };

    hideCreateForm = () => {
        this.setState({ showCreateForm: false, isPreparingCreateForm: false });
        this.resetBooking();
    };

    // Helper function to update form fields
    updateFormField = (field, value) => {
        this.setState((prevState) => ({
            [field]: value,
        }));
    };

    updateSurgeryDetails = (field, value) => {
        this.setState(
            (prevState) => ({
                surgeryDetails: {
                    ...prevState.surgeryDetails,
                    [field]: value,
                },
            }),
            () => {
                // If payor panel changed, clear existing surgery selections since they may not be valid for new payor
                if (field === "payorPanel") {
                    this.setState({
                        selectedSurgeriesFromAPI: [],
                        surgeryDetails: {
                            ...this.state.surgeryDetails,
                            selectedSurgeries: [],
                        },
                        surgeryRows: [],
                    });
                }
            }
        );
    };

    updateMedicalInfo = (field, value) => {
        this.setState(
            (prevState) => ({
                medicalInfo: {
                    ...prevState.medicalInfo,
                    [field]: value,
                },
            }),
            () => {
                // Recalculate all surgery costs when anesthesia changes
                if (field === "anesthesia") {
                    this.recalculateAllSurgeryCosts();
                }
            }
        );
    };

    // Recalculate estimated costs for all surgery rows when overall anesthesia changes
    recalculateAllSurgeryCosts = () => {
        this.setState((prev) => {
            const rows = [...prev.surgeryRows];
            rows.forEach((row, index) => {
                if (row.id) {
                    rows[index] = {
                        ...row,
                        estimatedCost: this.calculateEstimatedCost(row.id),
                    };
                }
            });
            return { surgeryRows: rows };
        });
    };

    // Handle multiple surgery selection from printed names API
    onSurgerySelect = (surgeryItem) => {
        const currentSelections = [...this.state.selectedSurgeriesFromAPI];
        const isAlreadySelected = currentSelections.find(
            (item) => item._id === surgeryItem._id
        );

        let newSelections;
        if (isAlreadySelected) {
            // Remove if already selected
            newSelections = currentSelections.filter(
                (item) => item._id !== surgeryItem._id
            );
        } else {
            // Add if not selected
            newSelections = [...currentSelections, surgeryItem];
        }

        // Create combined array with id, name, and payor information
        const selectedSurgeries = newSelections.map((item) => ({
            id: item._id,
            name: item.printedName,
            payor: item.payor,
        }));

        this.setState({
            selectedSurgeriesFromAPI: newSelections,
            surgeryDetails: {
                ...this.state.surgeryDetails,
                selectedSurgeries: selectedSurgeries,
            },
        });
    };

    // ----- New Surgery Rows (Tabular) APIs -----
    hasValidSurgeryRows = () => {
        const { surgeryRows } = this.state;
        if (!Array.isArray(surgeryRows) || surgeryRows.length === 0) return false;
        return surgeryRows.some((row) => row && row.id && row.laterality); // At least one valid surgery
    };

    hasAnySurgerySelected = () => {
        const { surgeryRows } = this.state;
        if (!Array.isArray(surgeryRows) || surgeryRows.length === 0) return false;
        return surgeryRows.some((row) => row && row.id); // At least one surgery selected (even without laterality)
    };

    hasIncompleteSurgeryRows = () => {
        const { surgeryRows } = this.state;
        if (!Array.isArray(surgeryRows) || surgeryRows.length === 0) return false;
        return surgeryRows.some((row) => {
            if (!row) {
                return true;
            }
            return !(row.id && row.laterality);
        });
    };

    addSurgeryRow = () => {
        this.setState((prev) => {
            const rows = Array.isArray(prev.surgeryRows) ? prev.surgeryRows : [];
            let defaultLaterality = "";
            for (let i = rows.length - 1; i >= 0; i -= 1) {
                if (rows[i]?.laterality) {
                    defaultLaterality = rows[i].laterality;
                    break;
                }
            }
            return {
                surgeryRows: [
                    ...rows,
                    { id: "", name: "", laterality: defaultLaterality, estimatedCost: 0 },
                ],
            };
        });
    };

    removeSurgeryRow = (index) => {
        this.setState((prev) => ({
            surgeryRows: prev.surgeryRows.filter((_, i) => i !== index),
        }));
    };

    updateSurgeryRow = (index, field, value) => {
        this.setState((prev) => {
            const rows = [...prev.surgeryRows];
            const current = rows[index] || {
                id: "",
                name: "",
                laterality: "",
                estimatedCost: 0,
            };
            if (field === "surgeryId") {
                const item =
                    (this.getFilteredSurgeryTypes() || []).find((p) => p._id === value) || {};
                const updatedRow = {
                    ...current,
                    id: item._id || "",
                    name: item.printedName || "",
                };
                // Recalculate estimated cost when surgery changes
                updatedRow.estimatedCost = this.calculateEstimatedCost(updatedRow.id);
                rows[index] = updatedRow;
            } else {
                rows[index] = { ...current, [field]: value };
            }
            return { surgeryRows: rows };
        });
    };

    // Clear all selected surgeries
    clearSurgerySelections = () => {
        this.setState({
            selectedSurgeriesFromAPI: [],
            surgeryDetails: {
                ...this.state.surgeryDetails,
                selectedSurgeries: [], // Clear the combined array
            },
        });
    };

    // Remove a single selected surgery when clicking the tag X inside the MultiSelect
    onRemoveSelectedSurgeryTag = (value, index) => {
        this.setState((prevState) => {
            const newSelections = prevState.selectedSurgeriesFromAPI.filter(
                (_, i) => i !== index
            );
            const selectedSurgeries = newSelections.map((item) => ({
                id: item._id,
                name: item.printedName,
                payor: item.payor,
            }));
            return {
                selectedSurgeriesFromAPI: newSelections,
                surgeryDetails: {
                    ...prevState.surgeryDetails,
                    selectedSurgeries,
                },
            };
        });
    };

    // Get filtered surgery types based on selected payor panel
    getFilteredSurgeryTypes = () => {
        const { printedNames, surgeryDetails } = this.state;
        const list = printedNames || [];

        if (!surgeryDetails.payorPanel) {
            // If no payor panel selected, show all
            return list;
        }

        const selectedKey = normalizePayorKey(surgeryDetails.payorPanel);
        const selectedIsCashLike = isCashLikePayorKey(surgeryDetails.payorPanel);

        return list.filter((surgery) => {
            const rowKey = normalizePayorKey(surgery.payor);
            if (rowKey === selectedKey) return true;
            // Align Cash / Self Pay / blank payor (master default) with cash-like panel choice
            if (selectedIsCashLike && isCashLikePayorKey(surgery.payor)) return true;
            return false;
        });
    };

    // Get anesthesia rate by name
    getAnesthesiaRate = (anesthesiaName) => {
        if (!anesthesiaName) return 0;
        const anesthesia = this.state.anesthesiaOptionsForSurgery.find(
            (opt) => opt.value === anesthesiaName
        );
        return anesthesia ? anesthesia.rate || 0 : 0;
    };

    // Calculate estimated cost for a surgery row (anesthesia is now a separate single charge, not per-surgery)
    calculateEstimatedCost = (surgeryId) => {
        const surgery = this.state.printedNames.find((s) => s._id === surgeryId);
        if (!surgery) return 0;

        const surgeryRate = surgery.surgeryRate || 0;

        // Apply MoU discount if available (applied on surgery rate only)
        const mouDiscountPercent = surgery.mouDiscount || 0;
        const mouDiscountAmount = (mouDiscountPercent / 100) * surgeryRate;

        // Apply Special discount if available
        const specialDiscountAmount = surgery.specialDiscount || 0;

        // Final calculation: surgery rate - MoU discount - Special discount
        return Math.round(surgeryRate - mouDiscountAmount - specialDiscountAmount);
    };

    // Filter and sort function for surgery types with relevance scoring
    filterAndSortSurgeryTypes = (query, surgeries) => {
        if (!query || !query.trim()) {
            // No query - return all surgeries sorted alphabetically
            return surgeries.sort((a, b) =>
                (a.printedName || "").localeCompare(b.printedName || "")
            );
        }

        const searchTerm = query.toLowerCase().trim();

        // Filter and score surgeries by relevance
        const scoredSurgeries = surgeries
            .filter((surgery) => {
                const surgeryName = (surgery.printedName || "").toLowerCase();
                return surgeryName.includes(searchTerm);
            })
            .map((surgery) => {
                const surgeryName = (surgery.printedName || "").toLowerCase();
                let score = 0;

                // Higher score for exact match
                if (surgeryName === searchTerm) {
                    score = 1000;
                }
                // High score for starts with
                else if (surgeryName.startsWith(searchTerm)) {
                    score = 500;
                }
                // Medium score for word boundary match
                else if (
                    surgeryName.includes(" " + searchTerm) ||
                    surgeryName.includes("-" + searchTerm)
                ) {
                    score = 300;
                }
                // Lower score for contains
                else if (surgeryName.includes(searchTerm)) {
                    score = 100;
                }

                // Boost score for shorter names (more specific matches)
                score += Math.max(0, 50 - surgeryName.length);

                return { ...surgery, searchScore: score };
            })
            .sort((a, b) => {
                // Sort by score (descending), then alphabetically
                if (b.searchScore !== a.searchScore) {
                    return b.searchScore - a.searchScore;
                }
                return (a.printedName || "").localeCompare(b.printedName || "");
            });

        return scoredSurgeries;
    };

    // ----- Aggregated totals across selected surgeries -----
    getSelectedSurgeryRecords = () => {
        const rows = Array.isArray(this.state.surgeryRows)
            ? this.state.surgeryRows
            : [];
        const records = [];
        rows.forEach((row) => {
            if (!row || !row.id) return;
            const rec = this.state.printedNames.find((p) => p._id === row.id);
            if (rec) records.push(rec);
        });
        return records;
    };

    computeAggregatedBilling = () => {
        const selected = this.getSelectedSurgeryRecords();
        const anesthesiaRate = this.getAnesthesiaRate(
            this.state.medicalInfo.anesthesia
        );

        const totals = selected.reduce(
            (acc, s) => {
                const surgeryRate = Number(s.surgeryRate || 0);
                const tissueProcessing = Number(s.tissueProcessing || 0);
                const femto = Number(s.femto || 0);
                const specialDiscountDefault = Number(s.specialDiscount || 0);
                const mouPercent = Number(s.mouDiscount || 0);

                acc.totalSurgeryRate += surgeryRate;
                acc.totalTissueProcessing += tissueProcessing;
                acc.totalFemto += femto;
                acc.totalSpecialDiscountDefault += specialDiscountDefault;

                // MoU discount is applied on surgeryRate only (not including anesthesia)
                const base = surgeryRate;
                const mouAmount = Math.round((mouPercent / 100) * base);
                acc.totalMouDiscount += mouAmount;

                return acc;
            },
            {
                totalSurgeryRate: 0,
                totalTissueProcessing: 0,
                totalFemto: 0,
                totalMouDiscount: 0,
                totalSpecialDiscountDefault: 0,
            }
        );

        const specialDiscountRupees = this.state.specialDiscountManuallyEdited
            ? Number(this.state.specialDiscountRupees || 0)
            : totals.totalSpecialDiscountDefault;

        // Anesthesia is a single charge, not per-surgery
        const gross =
            totals.totalSurgeryRate +
            anesthesiaRate +
            totals.totalTissueProcessing +
            totals.totalFemto;
        const totalDiscounts = totals.totalMouDiscount + specialDiscountRupees;
        const estimatedTotal = Math.max(0, Math.round(gross - totalDiscounts));

        return {
            ...totals,
            totalAnesthesiaCharges: anesthesiaRate, // Single value, not additive
            specialDiscountRupees,
            estimatedTotal,
        };
    };

    buildConsolidatedBillingSnapshot = () => {
        const agg = this.computeAggregatedBilling();
        const toPaise = (n) => Math.max(0, Math.round(Number(n || 0) * 100));

        // Note: Anesthesia is a single charge for the entire surgical session, not per-surgery
        const snapshot = {
            surgeryRatePaise: toPaise(agg.totalSurgeryRate),
            anaestheticChargesPaise: toPaise(agg.totalAnesthesiaCharges),
            tissueProcessingPaise: toPaise(agg.totalTissueProcessing),
            combinedRatePaise: toPaise(agg.estimatedTotal),
            procedureTaxPercent: 0,
            procedureCostRupees: 0,
            implantCostInclusivePaise: 0,
            implantTaxPercent: 0,
            femtoChargesPaise: toPaise(agg.totalFemto),
            specialDiscountPaise: toPaise(agg.specialDiscountRupees),
            specialDiscountRemarks: "",
            iolDiscountPaise: 0,
            mouDiscountPercent: 0,
            mouDiscountPaise: toPaise(agg.totalMouDiscount),
            selectedAnesthesiaId: this.state.medicalInfo?.anesthesia || "",
        };
        return snapshot;
    };

    // Filter function for surgery types (used by Suggest component)
    filterSurgeryTypes = (query, surgery) => {
        if (!query || !surgery.printedName) return true;

        const searchTerm = query.toLowerCase().trim();
        const surgeryName = surgery.printedName.toLowerCase();

        // Check if the surgery name contains the search term
        return surgeryName.includes(searchTerm);
    };

    // Render function for surgery items in multi-select
    renderSurgeryItem = (surgery, { handleClick, modifiers, query }) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }

        const isSelected = this.state.selectedSurgeriesFromAPI.find(
            (item) => item._id === surgery._id
        );

        return (
            <MenuItem
                key={surgery._id}
                active={modifiers.active}
                disabled={modifiers.disabled}
                onClick={handleClick}
                text={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <span>{surgery.printedName}</span>
                        {surgery.payor && (
                            <Tag minimal small intent="primary" style={{ marginLeft: "8px" }}>
                                {surgery.payor}
                            </Tag>
                        )}
                    </div>
                }
                icon={isSelected ? "tick" : "blank"}
                style={{ minWidth: "400px", whiteSpace: "nowrap" }}
            />
        );
    };

    // Render selected surgery tags
    renderSurgeryTag = (surgery) => {
        return surgery.printedName;
    };

    renderSurgeriesList = () => {
        const {
            surgeries,
            isLoading,
            quickFilterText,
            doctorFilter,
            branchFilter,
            scheduledDateFilter,
            selectedDoctorFilter,
            selectedBranchFilter,
        } = this.state;

        if (isLoading) {
            return (
                <NonIdealState
                    icon={<Spinner />}
                    title="Loading Surgeries..."
                    description="Please wait while we load the surgery data"
                />
            );
        }

        return (
            <div
                style={{
                    padding: "20px",
                    backgroundColor: "var(--bg-primary)",
                    color: "var(--text-primary)",
                    minHeight: "100vh",
                }}
            >
                {/* <div style={{ marginBottom: "20px" }}>
          <H1 style={{ color: "var(--text-primary)", margin: 0 }}>Surgery Booking Management</H1>
          <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
            View and manage all surgery bookings, create new bookings
          </p>
        </div> */}

                {/* Filters and Actions */}
                <Card
                    elevation={Elevation.TWO}
                    style={{ marginBottom: "20px", padding: "20px" }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "15px",
                        }}
                    >
                        <H3 style={{ color: "var(--text-primary)", margin: 0 }}>
                            Surgery Bookings
                        </H3>
                        <div style={{ display: "flex", gap: "12px" }}>
                            <SxPriceEstimator />
                            <Button large intent="primary" icon="add" onClick={this.showCreateForm}>
                                Create New Surgery
                            </Button>
                        </div>
                    </div>

                    {/* Filter Controls */}
                    <div
                        style={{
                            display: "flex",
                            gap: "15px",
                            flexWrap: "wrap",
                            alignItems: "center",
                        }}
                    >
                        {/* Text Search */}
                        <ControlGroup>
                            <Tag large minimal>
                                Search
                            </Tag>
                            <Tooltip
                                content={
                                    <div style={{ maxWidth: "350px" }}>
                                        <strong>Search across ALL surgeries:</strong>
                                        <br />
                                        <br />
                                        <strong>Searchable fields:</strong>
                                        <br />• Patient ID
                                        <br />• Patient Name & Phone
                                        <br />• Booking Reference
                                        <br />• Surgery Type & Group
                                        <br />• Urgency Level & Status
                                        <br />• Procedure Details
                                        <br />
                                        <br />
                                        <em>
                                            Note: Search works alongside date/branch/doctor filters, so results
                                            must satisfy every active filter.
                                        </em>
                                    </div>
                                }
                                position={Position.BOTTOM}
                                usePortal={false}
                            >
                                <InputGroup
                                    placeholder="Search across all surgeries (PID, name, phone, booking ref, etc.)..."
                                    value={quickFilterText}
                                    onChange={(e) =>
                                        this.handleFilterChange("quickFilterText", e.target.value)
                                    }
                                    style={{ width: "350px" }}
                                    leftIcon="search"
                                />
                            </Tooltip>
                        </ControlGroup>

                        {/* Doctor Filter */}
                        <ControlGroup>
                            <Tag large minimal>
                                Doctor
                            </Tag>
                            <SingleIDSelectWrapper
                                items={this.createDoctorItems()}
                                selectedItem={this.state.selectedDoctorFilter}
                                showSelectProp={true}
                                labelKey="formattedName"
                                placeholder="Type doctor name..."
                                width="250px"
                                onChange={this.handleDoctorFilterSelect}
                            />
                        </ControlGroup>

                        {/* Branch Filter */}
                        <ControlGroup>
                            <Tag large minimal>
                                Branch
                            </Tag>
                            <SingleSelectWrapper
                                items={this.createBranchItems()}
                                selectedItem={this.state.selectedBranchFilter}
                                showSelectProp={true}
                                placeholder="Type branch name..."
                                width="200px"
                                loading={this.state.isLoadingBranchesList}
                                emptyMessage="No branches available"
                                onChange={this.handleBranchFilterSelect}
                            />
                        </ControlGroup>

                        {/* Scheduled Date Filter */}
                        <ControlGroup>
                            <Tag large minimal>
                                Scheduled Date
                            </Tag>
                            <DateInput
                                formatDate={(d) => moment(d).format("DD/MM/YYYY")}
                                parseDate={(str) => moment(str, "DD/MM/YYYY").toDate()}
                                value={scheduledDateFilter}
                                onChange={(d) => {
                                    this.setState({ scheduledDateFilter: d }, () => {
                                        // Apply client-side filters when date changes (no API call needed)
                                        this.applyFilters();
                                    });
                                }}
                                popoverProps={{ minimal: true }}
                            />
                        </ControlGroup>

                        {/* Status Filter */}
                        <ControlGroup>
                            <Tag large minimal>
                                Status
                            </Tag>
                            <SingleSelectWrapper
                                items={this.createStatusItems()}
                                selectedItem={this.state.selectedStatusFilter}
                                showSelectProp={true}
                                placeholder="Select status..."
                                width="180px"
                                onChange={this.handleStatusFilterSelect}
                            />
                        </ControlGroup>

                        {/* Clear all filters */}
                        <Button
                            minimal
                            icon="cross"
                            onClick={this.clearAllFilters}
                            style={{ color: "var(--text-secondary)" }}
                        >
                            Clear Filters
                        </Button>

                    </div>
                </Card>

                {/* Surgeries Grid */}
                <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
                    {this.renderStatusCounts()}
                    <div
                        className="ag-theme-alpine-dark"
                        style={{ height: "600px", width: "100%" }}
                    >
                        <AgGridReact
                            columnDefs={this.columnDefs}
                            rowData={
                                Array.isArray(this.state.filteredSurgeries)
                                    ? this.state.filteredSurgeries
                                    : []
                            }
                            onGridReady={this.onGridReady}
                            defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true,
                            }}
                            pagination={true}
                            paginationPageSize={50}
                            rowSelection="single"
                            enableRangeSelection={true}
                            enableCharts={true}
                            sideBar={true}
                            statusBar={{
                                statusPanels: [
                                    {
                                        statusPanel: "agTotalAndFilteredRowCountComponent",
                                        align: "left",
                                    },
                                ],
                            }}
                            loading={isLoading}
                        />
                    </div>
                </Card>
            </div>
        );
    };

    renderBookingSummary = () => {
        const {
            selectedPatient,
            surgeryDetails,
            selectedDoctor,
            selectedBranch,
            selectedDate,
            selectedTimeSlot,
            medicalInfo,
        } = this.state;

        return (
            <Card
                className="v3-card"
                style={{
                    height: "fit-content",
                    position: "sticky",
                    top: "20px",
                    backgroundColor: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    width: "100%",
                }}
            >
                <div className="v3-card-body" style={{ padding: "12px 16px" }}>
                    {/* Patient Info */}
                    {selectedPatient && (
                        <div
                            style={{
                                marginBottom: "12px",
                                padding: "8px 12px",
                                backgroundColor: "rgba(15, 153, 96, 0.1)",
                                borderRadius: "4px",
                                border: "1px solid rgba(15, 153, 96, 0.3)",
                            }}
                        >
                            <Text
                                style={{
                                    fontWeight: "600",
                                    fontSize: "13px",
                                    color: "var(--text-primary)",
                                    margin: 0,
                                    marginBottom: "2px",
                                }}
                            >
                                {selectedPatient.name}
                            </Text>
                            <Text
                                style={{
                                    color: "var(--text-secondary)",
                                    fontSize: "11px",
                                    margin: 0,
                                }}
                            >
                                {selectedPatient.phone} • {selectedPatient.age}y •{" "}
                                {selectedPatient.sex === "M"
                                    ? "Male"
                                    : selectedPatient.sex === "F"
                                        ? "Female"
                                        : selectedPatient.sexLabel || ""}
                            </Text>
                            {this.state.patientAddress && (
                                <Text
                                    style={{
                                        color: "var(--text-secondary)",
                                        fontSize: "11px",
                                        margin: "2px 0 0 0",
                                        fontStyle: "italic",
                                    }}
                                >
                                    {this.state.patientAddress}
                                </Text>
                            )}
                        </div>
                    )}

                    {/* Doctor & Branch */}
                    {selectedPatient && selectedDoctor && selectedBranch && (
                        <div
                            style={{
                                marginBottom: "12px",
                                padding: "8px 12px",
                                backgroundColor: "rgba(15, 153, 96, 0.1)",
                                borderRadius: "4px",
                                border: "1px solid rgba(15, 153, 96, 0.3)",
                            }}
                        >
                            <Text
                                style={{
                                    fontSize: "12px",
                                    color: "var(--text-primary)",
                                    margin: 0,
                                    marginBottom: "2px",
                                }}
                            >
                                <span style={{ fontWeight: "600" }}>Doctor:</span> {selectedDoctor.name}
                            </Text>
                            <Text
                                style={{ fontSize: "11px", color: "var(--text-secondary)", margin: 0 }}
                            >
                                <span style={{ fontWeight: "500" }}>Branch:</span> {selectedBranch.name}
                            </Text>
                        </div>
                    )}

                    {/* Schedule */}
                    {selectedPatient &&
                        selectedDoctor &&
                        selectedBranch &&
                        selectedDate &&
                        selectedTimeSlot && (
                            <div
                                style={{
                                    marginBottom: "12px",
                                    padding: "8px 12px",
                                    backgroundColor: "rgba(15, 153, 96, 0.1)",
                                    borderRadius: "4px",
                                    border: "1px solid rgba(15, 153, 96, 0.3)",
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: "12px",
                                        color: "var(--text-primary)",
                                        margin: 0,
                                        marginBottom: "2px",
                                    }}
                                >
                                    <span style={{ fontWeight: "600" }}>Date:</span>{" "}
                                    {moment(selectedDate).format("DD/MM/YYYY")}
                                </Text>
                                <Text
                                    style={{ fontSize: "11px", color: "var(--text-secondary)", margin: 0 }}
                                >
                                    <span style={{ fontWeight: "500" }}>Time:</span>{" "}
                                    {selectedTimeSlot.timeFrame}
                                    {this.state.chosenTime && (
                                        <span>
                                            {" "}
                                            • {moment(this.state.chosenTime, "HH:mm").format("hh:mm A")}
                                        </span>
                                    )}
                                </Text>
                            </div>
                        )}

                    {/* Surgery Details */}
                    {selectedPatient &&
                        selectedDoctor &&
                        selectedBranch &&
                        selectedDate &&
                        selectedTimeSlot &&
                        this.hasValidSurgeryRows() && (
                            <div
                                style={{
                                    marginBottom: "12px",
                                    padding: "8px 12px",
                                    backgroundColor: "rgba(15, 153, 96, 0.1)",
                                    borderRadius: "4px",
                                    border: "1px solid rgba(15, 153, 96, 0.3)",
                                }}
                            >
                                {/* Payor and Payment */}
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "8px",
                                        marginBottom: "6px",
                                        fontSize: "11px",
                                    }}
                                >
                                    {surgeryDetails.payorPanel && (
                                        <Text style={{ color: "var(--text-primary)", margin: 0 }}>
                                            <span style={{ fontWeight: "600" }}>Payor:</span>{" "}
                                            {surgeryDetails.payorPanel}
                                        </Text>
                                    )}
                                    <Text style={{ color: "var(--text-secondary)", margin: 0 }}>
                                        • {surgeryDetails.cashSubsidy}
                                    </Text>
                                </div>

                                {/* Surgery List */}
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "4px",
                                        flexWrap: "wrap",
                                        marginBottom: "4px",
                                    }}
                                >
                                    {(this.state.surgeryRows || []).map((r, index) => {
                                        const surgery = this.state.printedNames.find((s) => s._id === r.id);
                                        return (
                                            <div
                                                key={`${r.id}-${index}`}
                                                style={{ display: "flex", gap: "2px", alignItems: "center" }}
                                            >
                                                <Tag minimal small style={{ fontSize: "9px", padding: "2px 6px" }}>
                                                    {r.name || "(Unknown)"}
                                                </Tag>
                                                {r.laterality && (
                                                    <Tag
                                                        minimal
                                                        small
                                                        intent="primary"
                                                        style={{ fontSize: "8px", padding: "1px 4px" }}
                                                    >
                                                        {_.startCase(r.laterality)}
                                                    </Tag>
                                                )}
                                                {typeof r.estimatedCost === "number" && (
                                                    <Tag
                                                        minimal
                                                        small
                                                        intent="success"
                                                        style={{ fontSize: "8px", padding: "1px 4px" }}
                                                    >
                                                        ₹{r.estimatedCost}
                                                    </Tag>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Overall Anesthesia */}
                                {medicalInfo.anesthesia && (
                                    <Text
                                        style={{
                                            fontSize: "10px",
                                            color: "var(--text-secondary)",
                                            margin: "4px 0 0 0",
                                        }}
                                    >
                                        <span style={{ fontWeight: "500" }}>Anesthesia:</span>{" "}
                                        {medicalInfo.anesthesia}
                                    </Text>
                                )}

                                {/* Urgency */}
                                {surgeryDetails.urgencyLevel && (
                                    <Text
                                        style={{
                                            fontSize: "10px",
                                            color: "var(--text-secondary)",
                                            margin: 0,
                                        }}
                                    >
                                        <span style={{ fontWeight: "500" }}>Urgency:</span>{" "}
                                        {_.startCase(surgeryDetails.urgencyLevel)}
                                    </Text>
                                )}
                            </div>
                        )}

                    {/* Medical Info */}
                    {selectedPatient &&
                        selectedDoctor &&
                        selectedBranch &&
                        selectedDate &&
                        selectedTimeSlot &&
                        this.hasValidSurgeryRows() && (
                            <div
                                style={{
                                    marginBottom: "12px",
                                    padding: "8px 12px",
                                    backgroundColor: "rgba(15, 153, 96, 0.1)",
                                    borderRadius: "4px",
                                    border: "1px solid rgba(15, 153, 96, 0.3)",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "8px",
                                        fontSize: "11px",
                                        marginBottom: "4px",
                                    }}
                                >
                                    <Text style={{ color: "var(--text-primary)", margin: 0 }}>
                                        <span style={{ fontWeight: "600" }}>Diet:</span>{" "}
                                        {medicalInfo.dietPreference
                                            ? _.startCase(medicalInfo.dietPreference)
                                            : "Regular"}
                                    </Text>
                                    <Text style={{ color: "var(--text-secondary)", margin: 0 }}>
                                        • <span style={{ fontWeight: "500" }}>Bed:</span>{" "}
                                        {medicalInfo.bedPreference
                                            ? _.startCase(medicalInfo.bedPreference)
                                            : "Bed"}
                                    </Text>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        gap: "40px",
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                    }}
                                >
                                    {medicalInfo.isDiabetic && (
                                        <Tag
                                            minimal
                                            intent="warning"
                                            style={{ fontSize: "9px", padding: "1px 4px" }}
                                        >
                                            Diabetic
                                        </Tag>
                                    )}
                                    {/* {medicalInfo.allergies && (
                    <Tag minimal style={{ fontSize: "9px", padding: "1px 4px" }}>
                      Allergies: {medicalInfo.allergies}
                    </Tag>
                  )}
                  {medicalInfo.currentMedications && (
                    <Tag minimal style={{ fontSize: "9px", padding: "1px 4px" }}>
                      Meds: {medicalInfo.currentMedications}
                    </Tag>
                  )} */}
                                </div>

                                {medicalInfo.additionalNotes && (
                                    <Text
                                        style={{
                                            fontSize: "10px",
                                            color: "var(--text-secondary)",
                                            margin: "4px 0 0 0",
                                        }}
                                    >
                                        <span style={{ fontWeight: "500" }}>Notes:</span>{" "}
                                        {medicalInfo.additionalNotes}
                                    </Text>
                                )}
                            </div>
                        )}

                    {/* Action Button */}
                    {selectedPatient &&
                        selectedDoctor &&
                        selectedBranch &&
                        selectedDate &&
                        selectedTimeSlot &&
                        this.hasValidSurgeryRows() && (
                            <div style={{ marginTop: "12px" }}>
                                <Button
                                    intent="primary"
                                    fill
                                    loading={this.state.isBooking}
                                    onClick={this.bookSurgery}
                                    disabled={!this.validateForm({ silent: true })}
                                    icon={this.state.isBooking ? "refresh" : "tick"}
                                    style={{
                                        height: "36px",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                    }}
                                >
                                    {this.state.isBooking ? "Booking..." : "Book Surgery"}
                                </Button>
                            </div>
                        )}
                </div>
            </Card>
        );
    };

    renderBookingForm = () => {
        const {
            selectedPatient,
            surgeryDetails,
            branches,
            selectedBranch,
            availableDoctors,
            selectedDoctor,
            selectedDate,
            selectedTime,
            selectedTimeSlot,
            doctorCapacity,
            isLoadingCapacity,
            medicalInfo,
            whatsappNumber,
            enableWhatsappConfirmation,
            enableEmailConfirmation,
            surgeryTypes,
            eyeOptions,
            urgencyOptions,
            anesthesiaOptions,
        } = this.state;

        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--v3-spacing-lg)",
                }}
            >
                {/* Step 1: Patient Search - Always visible */}
                <Card className="v3-card">
                    <div className="v3-card-header">
                        <H3 className="v3-card-title">1. Patient Information</H3>
                    </div>
                    <div className="v3-card-body">
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr",
                                gap: "var(--v3-spacing-md)",
                            }}
                        >
                            <FormGroup label="Search Patients *" inline className="v3-form-group">
                                <AsyncSelWrap
                                    ref={this.patientSearchRef}
                                    disabled={this.state.showEditForm}
                                    autoFocus={!this.state.showEditForm}
                                    placeholder={
                                        this.state.showEditForm
                                            ? "Patient selected for editing"
                                            : "Type patient name, phone, or ID..."
                                    }
                                    searching={this.state.oldSearching}
                                    items={_.take(this.state.oldSearchResults, 10)}
                                    selectedItem={this.state.showEditForm ? selectedPatient : null}
                                    update={(item) => this.searchPatients(item)}
                                    onChange={async (item) => {
                                        await this.selectPatient(item);
                                    }}
                                />
                            </FormGroup>
                        </div>

                        {selectedPatient && (
                            <div
                                style={{
                                    marginTop: "var(--v3-spacing-md)",
                                    display: "grid",
                                    gridTemplateColumns: "150px 1fr 150px 1fr",
                                    rowGap: "10px",
                                    columnGap: "16px",
                                    alignItems: "center",
                                }}
                            >
                                <Text style={{ color: "var(--text-secondary)", fontSize: 12 }}>
                                    Patient Name
                                </Text>
                                {this.state.isPatientDetailsEditable ? (
                                    <InputGroup
                                        large
                                        placeholder="Enter patient name..."
                                        value={this.state.patientName || ""}
                                        onChange={(e) => this.updateFormField("patientName", e.target.value)}
                                    />
                                ) : (
                                    <InputGroup large readOnly value={this.state.patientName || ""} />
                                )}

                                <Text style={{ color: "var(--text-secondary)", fontSize: 12 }}>
                                    Phone
                                </Text>
                                {this.state.isPatientDetailsEditable ? (
                                    <InputGroup
                                        large
                                        placeholder="Enter phone number..."
                                        value={this.state.patientPhone || ""}
                                        onChange={(e) => this.updateFormField("patientPhone", e.target.value)}
                                    />
                                ) : (
                                    <InputGroup large readOnly value={this.state.patientPhone || ""} />
                                )}

                                <Text style={{ color: "var(--text-secondary)", fontSize: 12 }}>
                                    Date of Birth
                                </Text>
                                {this.state.isPatientDetailsEditable ? (
                                    <DateInput
                                        formatDate={(d) => (d ? moment(d).format("DD/MM/YYYY") : "")}
                                        parseDate={(str) => (str ? moment(str, "DD/MM/YYYY").toDate() : null)}
                                        value={this.state.patientDob}
                                        onChange={(d) => this.updateFormField("patientDob", d)}
                                        minDate={new Date(1900, 0, 1)}
                                        maxDate={new Date()}
                                        popoverProps={{ minimal: true }}
                                        inputProps={{
                                            large: true,
                                            className: "v3-select",
                                            style: { width: "100%" },
                                        }}
                                        fill
                                    />
                                ) : (
                                    <InputGroup
                                        large
                                        readOnly
                                        value={
                                            this.state.patientDob
                                                ? moment(this.state.patientDob).format("DD/MM/YYYY")
                                                : ""
                                        }
                                        style={{ width: "100%" }}
                                    />
                                )}

                                <Text style={{ color: "var(--text-secondary)", fontSize: 12 }}>
                                    Sex
                                </Text>
                                {this.state.isPatientDetailsEditable ? (
                                    <SingleSelectWrapper
                                        items={["Male", "Female", "Other"]}
                                        selectedItem={(() => {
                                            const sx = this.state.patientSex;
                                            if (sx === "M") return "Male";
                                            if (sx === "F") return "Female";
                                            if (sx === "O") return "Other";
                                            return null;
                                        })()}
                                        placeholder="Select sex..."
                                        onChange={(val) => {
                                            const code = val === "Male" ? "M" : val === "Female" ? "F" : "O";
                                            this.updateFormField("patientSex", code);
                                        }}
                                        width="100%"
                                        inputProps={{ large: true, className: "v3-select" }}
                                    />
                                ) : (
                                    <InputGroup
                                        large
                                        readOnly
                                        value={(() => {
                                            const sx = this.state.patientSex;
                                            if (sx === "M") return "Male";
                                            if (sx === "F") return "Female";
                                            if (sx === "O") return "Other";
                                            return "";
                                        })()}
                                        style={{ width: "100%" }}
                                    />
                                )}

                                <Text style={{ color: "var(--text-secondary)", fontSize: 12 }}>
                                    Patient Address
                                </Text>
                                {this.state.isPatientDetailsEditable ? (
                                    <InputGroup
                                        inputRef={this.patientAddressRef}
                                        large
                                        placeholder="Enter patient address..."
                                        value={this.state.patientAddress || ""}
                                        onChange={(e) =>
                                            this.updateFormField("patientAddress", e.target.value)
                                        }
                                    />
                                ) : (
                                    <InputGroup large readOnly value={this.state.patientAddress || ""} />
                                )}

                                <div />
                                <div style={{ display: "flex", gap: 8 }}>
                                    <Button
                                        small
                                        minimal
                                        icon={this.state.isPatientDetailsEditable ? "confirm" : "edit"}
                                        intent={this.state.isPatientDetailsEditable ? "success" : "primary"}
                                        onClick={() =>
                                            this.setState({
                                                isPatientDetailsEditable: !this.state.isPatientDetailsEditable,
                                            })
                                        }
                                    >
                                        {this.state.isPatientDetailsEditable
                                            ? "Done"
                                            : "Edit Patient Details"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>

                {/* Step 2: Doctor & Branch - Visible after patient selection */}
                {selectedPatient && (
                    <Card className="v3-card">
                        <div className="v3-card-header">
                            <H3 className="v3-card-title">2. Branch & Doctor</H3>
                        </div>
                        <div className="v3-card-body">
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 1fr",
                                    gap: "var(--v3-spacing-lg)",
                                }}
                            >
                                {/* Branch Selection */}
                                <div>
                                    <FormGroup label="Select Branch *" inline>
                                        <SingleSelectWrapper
                                            ref={this.branchSelectRef}
                                            items={this.createBranchSelectItems()}
                                            selectedItem={selectedBranch?.name || null}
                                            placeholder="Search branches..."
                                            loading={this.state.isLoadingBranchesList}
                                            emptyMessage="Branch list unavailable"
                                            onChange={this.handleBranchSelect}
                                            width="100%"
                                            noResultsText="No branches found"
                                            inputProps={{
                                                large: true,
                                                className: "v3-select",
                                            }}
                                        />
                                    </FormGroup>
                                </div>

                                {/* Doctor Selection */}
                                <div>
                                    <FormGroup label="Select Doctor *" inline>
                                        <SingleSelectWrapper
                                            items={this.createDoctorSelectItems()}
                                            selectedItem={this.getFormattedDoctorName(selectedDoctor)}
                                            placeholder={
                                                selectedBranch ? "Search doctors..." : "Select Branch First"
                                            }
                                            disabled={!selectedBranch}
                                            loading={this.state.isLoadingDoctorsList}
                                            emptyMessage="No doctors available"
                                            onChange={this.handleDoctorSelect}
                                            width="100%"
                                            noResultsText="No doctors found"
                                            inputProps={{
                                                large: true,
                                                className: "v3-select",
                                            }}
                                        />
                                    </FormGroup>
                                </div>

                                {/* Advised By (required) */}
                                <div>
                                    <FormGroup label="Advised By *" inline>
                                        <SingleSelectWrapper
                                            items={this.createDoctorSelectItems()}
                                            selectedItem={this.getFormattedDoctorName(
                                                this.state.selectedAdvisedBy
                                            )}
                                            placeholder={"Search advising doctor..."}
                                            loading={this.state.isLoadingDoctorsList}
                                            emptyMessage="No doctors available"
                                            onChange={this.handleAdvisedBySelect}
                                            width="100%"
                                            noResultsText="No doctors found"
                                            inputProps={{
                                                large: true,
                                                className: "v3-select",
                                            }}
                                        />
                                    </FormGroup>
                                </div>
                            </div>
                        </div>
                    </Card>
                )}

                {/* Step 3: Schedule - Visible after doctor selection */}
                {selectedPatient && selectedDoctor && selectedBranch && (
                    <Card className="v3-card">
                        <div className="v3-card-header">
                            <H3 className="v3-card-title">3. Schedule Surgery</H3>
                        </div>
                        <div className="v3-card-body">
                            <SurgeryCalendar
                                doctorId={selectedDoctor._id}
                                branchId={selectedBranch._id}
                                branchName={selectedBranch.name}
                                onDateSelect={this.onDateSelect}
                                onSlotSelect={this.onTimeSlotSelect}
                                chosenTime={this.state.chosenTime}
                                onChosenTimeChange={(value) =>
                                    this.updateFormField("chosenTime", value)
                                }
                            />
                            {/* Chosen Time picker appears only inside SurgeryCalendar */}
                        </div>
                    </Card>
                )}

                {/* Step 4: Surgery Details - Visible after schedule selection */}
                {selectedPatient &&
                    selectedDoctor &&
                    selectedBranch &&
                    selectedDate &&
                    selectedTimeSlot && (
                        <Card className="v3-card">
                            <div className="v3-card-header">
                                <H3 className="v3-card-title">4. Surgery Details</H3>
                            </div>
                            <div className="v3-card-body">
                                {/* Row: Payor Panel, Cash/Subsidy, Urgency */}
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "var(--v3-spacing-lg)",
                                        marginBottom: "var(--v3-spacing-lg)",
                                    }}
                                >
                                    <FormGroup
                                        label="Payor Panel"
                                        className="v3-form-group"
                                        style={{ flex: 2 }}
                                    >
                                        <SingleSelectWrapper
                                            items={this.createPayorPanelItems()}
                                            selectedItem={this.state.selectedPayorPanel}
                                            placeholder={
                                                this.state.isLoadingPayorPanels
                                                    ? "Loading payor panels..."
                                                    : "Search payor panels..."
                                            }
                                            disabled={this.state.isLoadingPayorPanels}
                                            loading={this.state.isLoadingPayorPanels}
                                            emptyMessage="No payor panels available"
                                            onChange={this.handlePayorPanelSelect}
                                            width="100%"
                                            noResultsText="No payor panels found"
                                            inputProps={{
                                                large: true,
                                                className: "v3-select",
                                            }}
                                        />
                                    </FormGroup>

                                    <FormGroup
                                        label="Cash/Subsidy *"
                                        className="v3-form-group"
                                        style={{ flex: 1 }}
                                    >
                                        <SingleSelectWrapper
                                            items={this.createCashSubsidyItems()}
                                            selectedItem={
                                                this.state.cashSubsidyOptions.find(
                                                    (o) => o.value === surgeryDetails.cashSubsidy
                                                )?.label || null
                                            }
                                            placeholder="Search cash/subsidy options..."
                                            onChange={this.handleCashSubsidySelect}
                                            width="100%"
                                            noResultsText="No options found"
                                            inputProps={{
                                                large: true,
                                                className: "v3-select",
                                            }}
                                        />
                                    </FormGroup>

                                    <FormGroup
                                        label="Urgency"
                                        className="v3-form-group"
                                        style={{ flex: 1 }}
                                    >
                                        <SingleSelectWrapper
                                            items={this.createUrgencyItems()}
                                            selectedItem={(() => {
                                                const urgencyMap = {
                                                    routine: "Routine",
                                                    urgent: "Urgent",
                                                    emergency: "Emergency",
                                                };
                                                return urgencyMap[surgeryDetails.urgencyLevel] || null;
                                            })()}
                                            placeholder="Search urgency levels..."
                                            onChange={this.handleUrgencySelect}
                                            width="100%"
                                            noResultsText="No urgency levels found"
                                            inputProps={{
                                                large: true,
                                                className: "v3-select",
                                            }}
                                        />
                                    </FormGroup>
                                </div>

                                {/* Surgeries Cards */}
                                <div
                                    style={{
                                        marginBottom: "var(--v3-spacing-lg)",
                                        padding: 12,
                                        backgroundColor: "rgba(19, 124, 189, 0.06)",
                                        border: "1px solid rgba(19, 124, 189, 0.3)",
                                        borderRadius: 8,
                                        boxShadow: "0 2px 8px rgba(19, 124, 189, 0.15)",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: 12,
                                        }}
                                    >
                                        <H4
                                            style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}
                                        >
                                            <Icon icon="list" size={14} color="#137cbd" />
                                            Selected Surgeries
                                            {surgeryDetails.payorPanel && !this.state.isLoadingPrintedNames && (
                                                <span className="v3-text-secondary">
                                                    {" "}
                                                    (Filtered by {surgeryDetails.payorPanel})
                                                </span>
                                            )}
                                        </H4>
                                        <Button
                                            small
                                            intent="primary"
                                            icon="add"
                                            onClick={this.addSurgeryRow}
                                            disabled={!surgeryDetails.payorPanel}
                                        >
                                            Add Surgery
                                        </Button>
                                    </div>
                                    {!surgeryDetails.payorPanel && (
                                        <Text
                                            className="v3-text-secondary"
                                            style={{ fontStyle: "italic", marginBottom: 12, display: "block" }}
                                        >
                                            Select a Payor Panel to add surgeries
                                        </Text>
                                    )}

                                    {/* Surgery Cards */}
                                    {(this.state.surgeryRows || []).length === 0 ? (
                                        <div
                                            style={{
                                                padding: 20,
                                                textAlign: "center",
                                                backgroundColor: "var(--bg-secondary)",
                                                borderRadius: 6,
                                                border: "1px dashed var(--border-color)",
                                            }}
                                        >
                                            <Text className="v3-text-secondary">No surgeries added yet.</Text>
                                        </div>
                                    ) : (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                            {(this.state.surgeryRows || []).map((row, idx) => {
                                                const surgery = this.state.printedNames.find(
                                                    (s) => s._id === row.id
                                                );

                                                return (
                                                    <Card
                                                        key={idx}
                                                        style={{
                                                            backgroundColor: "var(--bg-surface)",
                                                            border: "1px solid var(--border-color)",
                                                            borderRadius: 8,
                                                            padding: 12,
                                                            position: "relative",
                                                        }}
                                                    >
                                                        {/* Header: Surgery name, laterality, group, and delete button */}
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "flex-start",
                                                                marginBottom: 8,
                                                            }}
                                                        >
                                                            <div style={{ flex: 1 }}>
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 8,
                                                                        marginBottom: 6,
                                                                    }}
                                                                >
                                                                    {/* Surgery Selection */}
                                                                    <div style={{ flex: 3, minWidth: 0 }}>
                                                                        {this.renderSurgerySelector(idx, surgery)}
                                                                    </div>

                                                                    {/* Laterality Selection */}
                                                                    <div style={{ flex: 1, minWidth: 0, marginLeft: 8 }}>
                                                                        <SingleSelectWrapper
                                                                            items={this.createLateralityItems()}
                                                                            selectedItem={(() => {
                                                                                const lateralityMap = {
                                                                                    left: "Left Eye",
                                                                                    right: "Right Eye",
                                                                                    both: "Both Eyes",
                                                                                };
                                                                                return lateralityMap[row.laterality] || null;
                                                                            })()}
                                                                            placeholder="Select eye..."
                                                                            onChange={(selectedLaterality) =>
                                                                                this.handleLateralitySelect(selectedLaterality, idx)
                                                                            }
                                                                            width="100%"
                                                                            noResultsText="No options found"
                                                                            inputProps={{
                                                                                large: true,
                                                                                className: "v3-select",
                                                                                style: { minWidth: "140px" },
                                                                            }}
                                                                        />
                                                                    </div>

                                                                    {/* Group Tag */}
                                                                    {surgery && surgery.group && (
                                                                        <Tag
                                                                            minimal
                                                                            intent="primary"
                                                                            style={{
                                                                                fontSize: "10px",
                                                                                backgroundColor: "rgba(19, 124, 189, 0.1)",
                                                                                color: "#137cbd",
                                                                                border: "1px solid rgba(19, 124, 189, 0.2)",
                                                                                flexShrink: 0,
                                                                                alignSelf: "flex-start",
                                                                            }}
                                                                        >
                                                                            {surgery.group}
                                                                        </Tag>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <Button
                                                                minimal
                                                                small
                                                                icon="trash"
                                                                intent="danger"
                                                                onClick={() => this.removeSurgeryRow(idx)}
                                                                style={{ marginLeft: 8 }}
                                                            />
                                                        </div>

                                                        {/* Financial Information */}
                                                        {surgery && (
                                                            <div
                                                                style={{
                                                                    backgroundColor: "rgba(15, 153, 96, 0.05)",
                                                                    border: "1px solid rgba(15, 153, 96, 0.2)",
                                                                    borderRadius: 6,
                                                                    padding: 8,
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        display: "grid",
                                                                        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                                                                        gap: 6,
                                                                        marginBottom: 6,
                                                                    }}
                                                                >
                                                                    <div>
                                                                        <Text
                                                                            style={{
                                                                                fontSize: "11px",
                                                                                color: "var(--text-secondary)",
                                                                                margin: 0,
                                                                            }}
                                                                        >
                                                                            Surgery Rate
                                                                        </Text>
                                                                        <Text
                                                                            style={{
                                                                                fontSize: "12px",
                                                                                fontWeight: 600,
                                                                                color: "var(--text-primary)",
                                                                                margin: 0,
                                                                            }}
                                                                        >
                                                                            ₹ {(surgery.surgeryRate || 0).toLocaleString()}
                                                                        </Text>
                                                                    </div>

                                                                    <div>
                                                                        <Text
                                                                            style={{
                                                                                fontSize: "11px",
                                                                                color: "var(--text-secondary)",
                                                                                margin: 0,
                                                                            }}
                                                                        >
                                                                            MoU Discount ({surgery.mouDiscount || 0}%)
                                                                        </Text>
                                                                        <Text
                                                                            style={{
                                                                                fontSize: "12px",
                                                                                fontWeight: 600,
                                                                                color: "#D9822B",
                                                                                margin: 0,
                                                                            }}
                                                                        >
                                                                            -₹{" "}
                                                                            {(() => {
                                                                                const surgeryRate = surgery.surgeryRate || 0;
                                                                                const discountAmount =
                                                                                    ((surgery.mouDiscount || 0) / 100) * surgeryRate;
                                                                                return Math.round(discountAmount).toLocaleString();
                                                                            })()}
                                                                        </Text>
                                                                    </div>

                                                                    <div>
                                                                        <Text
                                                                            style={{
                                                                                fontSize: "11px",
                                                                                color: "var(--text-secondary)",
                                                                                margin: 0,
                                                                            }}
                                                                        >
                                                                            Special Discount
                                                                        </Text>
                                                                        <Text
                                                                            style={{
                                                                                fontSize: "12px",
                                                                                fontWeight: 600,
                                                                                color: "#D9822B",
                                                                                margin: 0,
                                                                            }}
                                                                        >
                                                                            -₹ {(surgery.specialDiscount || 0).toLocaleString()}
                                                                        </Text>
                                                                    </div>
                                                                </div>

                                                                {/* Per-card estimated total and disclaimer moved to aggregate section below */}
                                                            </div>
                                                        )}
                                                    </Card>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Aggregated totals across all selected surgeries */}
                                {(this.state.surgeryRows || []).length > 0 && (
                                    <div
                                        style={{
                                            marginTop: 12,
                                            padding: 12,
                                            backgroundColor: "rgba(15, 153, 96, 0.05)",
                                            border: "1px solid rgba(15, 153, 96, 0.2)",
                                            borderRadius: 6,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 8,
                                        }}
                                    >
                                        {(() => {
                                            const agg = this.computeAggregatedBilling();
                                            return (
                                                <>
                                                    <div
                                                        style={{
                                                            display: "grid",
                                                            gridTemplateColumns: "repeat(4, 1fr)",
                                                            gap: 10,
                                                            alignItems: "end",
                                                        }}
                                                    >
                                                        <div>
                                                            <Text
                                                                style={{
                                                                    fontSize: "11px",
                                                                    color: "var(--text-secondary)",
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                Total Surgery Rate
                                                            </Text>
                                                            <Text style={{ fontSize: "14px", fontWeight: 700, margin: 0 }}>
                                                                ₹ {agg.totalSurgeryRate.toLocaleString()}
                                                            </Text>
                                                        </div>
                                                        <div>
                                                            <Text
                                                                style={{
                                                                    fontSize: "11px",
                                                                    color: "var(--text-secondary)",
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                Anesthesia Charges (Single)
                                                            </Text>
                                                            <Text style={{ fontSize: "14px", fontWeight: 700, margin: 0 }}>
                                                                ₹ {agg.totalAnesthesiaCharges.toLocaleString()}
                                                            </Text>
                                                        </div>
                                                        <div>
                                                            <Text
                                                                style={{
                                                                    fontSize: "11px",
                                                                    color: "var(--text-secondary)",
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                Total MoU Discount
                                                            </Text>
                                                            <Text
                                                                style={{
                                                                    fontSize: "14px",
                                                                    fontWeight: 700,
                                                                    color: "#D9822B",
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                -₹ {agg.totalMouDiscount.toLocaleString()}
                                                            </Text>
                                                        </div>
                                                        <div>
                                                            <Text
                                                                style={{
                                                                    fontSize: "11px",
                                                                    color: "var(--text-secondary)",
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                Special Discount (editable)
                                                            </Text>
                                                            <NumericInput
                                                                majorStepSize={1000}
                                                                min={0}
                                                                allowNumericCharactersOnly
                                                                buttonPosition="none"
                                                                value={
                                                                    this.state.specialDiscountManuallyEdited
                                                                        ? this.state.specialDiscountRupees || 0
                                                                        : agg.specialDiscountRupees
                                                                }
                                                                onValueChange={(val) => {
                                                                    const next = Number(val || 0);
                                                                    this.setState({
                                                                        specialDiscountRupees: Number.isFinite(next) ? next : 0,
                                                                        specialDiscountManuallyEdited: true,
                                                                    });
                                                                }}
                                                                style={{ width: 100 }}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div
                                                        style={{
                                                            borderTop: "1px solid rgba(15, 153, 96, 0.2)",
                                                            paddingTop: 10,
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Text style={{ fontSize: "14px", fontWeight: 700, margin: 0 }}>
                                                            Estimated Total
                                                        </Text>
                                                        <Text
                                                            style={{
                                                                fontSize: "16px",
                                                                fontWeight: 800,
                                                                color: "#0F9960",
                                                                margin: 0,
                                                            }}
                                                        >
                                                            ₹{" "}
                                                            {this.computeAggregatedBilling().estimatedTotal.toLocaleString()}
                                                        </Text>
                                                    </div>

                                                    <div
                                                        style={{
                                                            backgroundColor: "rgba(255, 193, 7, 0.1)",
                                                            border: "1px solid rgba(255, 193, 7, 0.3)",
                                                            borderRadius: 4,
                                                            padding: "8px 10px",
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: 6,
                                                        }}
                                                    >
                                                        <Icon
                                                            icon="info-sign"
                                                            size={12}
                                                            style={{ color: "#F29D49", marginTop: 1, flexShrink: 0 }}
                                                        />
                                                        <Text
                                                            style={{
                                                                fontSize: "10px",
                                                                lineHeight: 1.4,
                                                                color: "var(--text-secondary)",
                                                                margin: 0,
                                                            }}
                                                        >
                                                            <strong>Disclaimer:</strong> This is an estimate and may vary
                                                            based on IOL selection and special discounts. Please refer to the
                                                            Billing tab after surgery booking for actual costs.
                                                        </Text>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}

                                {/* Duration removed */}

                                {/* Procedure Details - Full width */}
                                {/* <FormGroup label="Notes">
                <TextArea
                  large
                  placeholder="Enter procedure specific notes..."
                  value={surgeryDetails.procedure}
                  onChange={(e) => this.updateSurgeryDetails("procedure", e.target.value)}
                  rows={3}
                  className="v3-textarea"
                  style={{ width: "100%" }}
                />
              </FormGroup> */}
                            </div>
                        </Card>
                    )}

                {/* Step 5: Medical Information - Visible after at least one surgery is added */}
                {selectedPatient &&
                    selectedDoctor &&
                    selectedBranch &&
                    selectedDate &&
                    selectedTimeSlot &&
                    this.state.surgeryRows &&
                    this.state.surgeryRows.length > 0 && (
                        <Card className="v3-card">
                            <div className="v3-card-header">
                                <H3 className="v3-card-title">5. Medical Information</H3>
                            </div>
                            <div className="v3-card-body">
                                {/* Medical Information - 3x4 Grid */}
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                        gridTemplateRows: "auto auto auto",
                                        columnGap: "var(--v3-spacing-md)",
                                        rowGap: "var(--v3-spacing-xxl)", // Increased row gap
                                        padding: "var(--v3-spacing-lg)",
                                        backgroundColor: "rgba(15, 153, 96, 0.02)",
                                        border: "1px solid rgba(15, 153, 96, 0.1)",
                                        borderRadius: "8px",
                                    }}
                                >
                                    {/* Row 0, Col 0: Anesthesia Type */}
                                    <div style={{ gridColumn: "1", gridRow: "1" }}>
                                        <FormGroup label="Anesthesia Type *" style={{ margin: 0 }}>
                                            <SingleSelectWrapper
                                                items={this.createAnesthesiaItems()}
                                                selectedItem={(() => {
                                                    const option = this.state.anesthesiaOptionsForSurgery.find(
                                                        (o) => o.value === medicalInfo.anesthesia
                                                    );
                                                    return option ? option.label : null;
                                                })()}
                                                placeholder={
                                                    this.state.isLoadingAnesthesiaForSurgery
                                                        ? "Loading anesthesia types..."
                                                        : "Search anesthesia types..."
                                                }
                                                disabled={this.state.isLoadingAnesthesiaForSurgery}
                                                loading={this.state.isLoadingAnesthesiaForSurgery}
                                                emptyMessage="No anesthesia options available"
                                                onChange={this.handleAnesthesiaSelect}
                                                width="100%"
                                                noResultsText="No anesthesia types found"
                                                inputProps={{
                                                    large: true,
                                                    className: "v3-select",
                                                }}
                                            />
                                        </FormGroup>
                                    </div>

                                    {/* Row 0, Col 1: Diet Preference */}
                                    <div style={{ gridColumn: "2", gridRow: "1" }}>
                                        <FormGroup label="Diet Preference" style={{ margin: 0 }}>
                                            <SingleSelectWrapper
                                                items={this.createDietItems()}
                                                selectedItem={(() => {
                                                    const dietMap = {
                                                        regular: "Regular Diet",
                                                        diabetic: "Diabetic Diet",
                                                        vegetarian: "Vegetarian",
                                                        vegan: "Vegan",
                                                        special: "Special Diet",
                                                    };
                                                    return dietMap[medicalInfo.dietPreference] || null;
                                                })()}
                                                placeholder="Search diet preferences..."
                                                onChange={this.handleDietSelect}
                                                width="100%"
                                                noResultsText="No diet options found"
                                                inputProps={{
                                                    large: true,
                                                    className: "v3-select",
                                                }}
                                            />
                                        </FormGroup>
                                    </div>

                                    {/* Row 0, Col 2: Bed Preference */}
                                    <div style={{ gridColumn: "3", gridRow: "1" }}>
                                        <FormGroup label="Bed Preference" style={{ margin: 0 }}>
                                            <SingleSelectWrapper
                                                items={this.createBedItems()}
                                                selectedItem={medicalInfo.bedPreference || null}
                                                placeholder="Search bed options..."
                                                onChange={this.handleBedSelect}
                                                width="100%"
                                                noResultsText="No bed options found"
                                                inputProps={{
                                                    large: true,
                                                    className: "v3-select",
                                                }}
                                            />
                                        </FormGroup>
                                    </div>

                                    {/* Row 0, Col 3: Diabetic Status */}
                                    <div style={{ gridColumn: "4", gridRow: "1" }}>
                                        <FormGroup label="Patient is Diabetic" style={{ margin: 0 }}>
                                            <SingleSelectWrapper
                                                items={["Yes", "No"]}
                                                selectedItem={medicalInfo.isDiabetic ? "Yes" : "No"}
                                                placeholder="Select diabetic status..."
                                                onChange={(selectedOption) => {
                                                    this.updateMedicalInfo("isDiabetic", selectedOption === "Yes");
                                                }}
                                                width="100%"
                                                noResultsText="No options found"
                                                inputProps={{
                                                    large: true,
                                                    className: "v3-select",
                                                }}
                                            />
                                        </FormGroup>
                                    </div>

                                    {/* Row 1, Col 0: Allergies */}
                                    {/* <div style={{ gridColumn: "1", gridRow: "2" }}>
                    <FormGroup label="Allergies" style={{ margin: 0 }}>
                      <TextArea
                        large
                        placeholder="List any known allergies..."
                        value={medicalInfo.allergies}
                        onChange={(e) => this.updateMedicalInfo("allergies", e.target.value)}
                        rows={3}
                        style={{ resize: "vertical" }}
                      />
                    </FormGroup>
                  </div> */}

                                    {/* Row 1, Col 1: Current Medications */}
                                    {/* <div style={{ gridColumn: "2", gridRow: "2" }}>
                    <FormGroup label="Current Medications" style={{ margin: 0 }}>
                      <TextArea
                        large
                        placeholder="List current medications..."
                        value={medicalInfo.currentMedications}
                        onChange={(e) => this.updateMedicalInfo("currentMedications", e.target.value)}
                        rows={3}
                        style={{ resize: "vertical" }}
                      />
                    </FormGroup>
                  </div> */}

                                    {/* Row 1, Col 2-3: Additional Notes (spans 2 columns) */}
                                    <div style={{ gridColumn: "1 / 5", gridRow: "2" }}>
                                        <FormGroup label="Additional Notes" style={{ margin: 0 }}>
                                            <TextArea
                                                large
                                                placeholder="Any additional medical notes..."
                                                value={medicalInfo.additionalNotes}
                                                onChange={(e) =>
                                                    this.updateMedicalInfo("additionalNotes", e.target.value)
                                                }
                                                cols={100}
                                                rows={3}
                                                style={{ resize: "vertical" }}
                                            />
                                        </FormGroup>
                                    </div>

                                    {/* Row 2, Col 0: WhatsApp Number */}
                                    {/* <div style={{ gridColumn: "1", gridRow: "3" }}>
                    <Text
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "var(--text-primary)",
                        marginBottom: "8px",
                        display: "block",
                      }}
                    >
                      WhatsApp Number
                    </Text>
                    <InputGroup
                      large
                      leftIcon="phone"
                      placeholder="+91 xxxxx xxxxx"
                      value={whatsappNumber}
                      onChange={(e) => this.updateFormField("whatsappNumber", e.target.value)}
                    />
                  </div> */}

                                    {/* Row 2, Col 1: Confirmation Preferences */}
                                    {/* <div style={{ gridColumn: "2", gridRow: "3" }}>
                    <Text
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "var(--text-primary)",
                        marginBottom: "8px",
                        display: "block",
                      }}
                    >
                      Confirmation Preferences
                    </Text>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <Switch
                        checked={enableWhatsappConfirmation}
                        onChange={(e) => this.updateFormField("enableWhatsappConfirmation", e.currentTarget.checked)}
                        label="WhatsApp Confirmation"
                        style={{ margin: 0 }}
                      />
                      <Switch
                        checked={enableEmailConfirmation}
                        onChange={(e) => this.updateFormField("enableEmailConfirmation", e.currentTarget.checked)}
                        label="Email Confirmation"
                        style={{ margin: 0 }}
                      />
                    </div>
                  </div> */}

                                    {/* Row 2, Col 2-3: Disclaimer (spans 2 columns) */}
                                    {/* <div style={{ gridColumn: "3 / 5", gridRow: "3" }}>
                    <Callout
                      intent="primary"
                      style={{
                        margin: 0,
                        fontSize: "12px",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                        <Icon icon="mobile-phone" size={16} style={{ marginTop: "2px", flexShrink: 0 }} />
                        <div>
                          <Text style={{ fontSize: "12px", fontWeight: "600", margin: 0, marginBottom: "4px" }}>
                            WhatsApp Integration
                          </Text>
                          <Text style={{ fontSize: "11px", margin: 0, lineHeight: "1.4" }}>
                            Automated confirmation and reminder messages will be sent if enabled. Please ensure the
                            WhatsApp number is correct and active.
                          </Text>
                        </div>
                      </div>
                    </Callout>
                  </div> */}
                                </div>
                            </div>
                        </Card>
                    )}
            </div>
        );
    };

    renderCreateFormModal = () => {
        const { showCreateForm, isPreparingCreateForm } = this.state;

        if (!showCreateForm) return null;

        return (
            <Dialog
                isOpen={showCreateForm}
                onClose={this.hideCreateForm}
                title="Create New Surgery Booking"
                style={{ width: "90%", maxWidth: "1400px", height: "90vh" }}
                className="bp3-dark"
                canOutsideClickClose={false}
                canEscapeKeyClose={true}
                isCloseButtonShown={true}
            >
                <div className={Classes.DIALOG_BODY} style={{ padding: 0, height: "100%" }}>
                    {isPreparingCreateForm ? (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                            }}
                        >
                            <NonIdealState
                                icon={<Spinner />}
                                title="Preparing booking form"
                                description="Fetching the latest doctor, branch, payor and surgery lists..."
                            />
                        </div>
                    ) : (
                        <div style={{ display: "flex", height: "100%" }}>
                            <div style={{ flex: 1, padding: "20px", overflowY: "auto" }}>
                                {this.renderBookingForm()}
                            </div>
                            <div
                                style={{
                                    width: "400px",
                                    minWidth: "400px",
                                    maxWidth: "400px",
                                    padding: "20px",
                                    borderLeft: "1px solid var(--border-color)",
                                    overflowY: "auto",
                                }}
                            >
                                {this.renderBookingSummary()}
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>
        );
    };

    renderEditFormModal = () => {
        const { showEditForm, isUpdating } = this.state;

        if (!showEditForm) return null;

        return (
            <Dialog
                isOpen={showEditForm}
                onClose={this.hideEditForm}
                title="Edit Surgery Booking"
                style={{ width: "90%", maxWidth: "1400px", height: "90vh" }}
                className="bp3-dark"
            >
                <div className={Classes.DIALOG_BODY} style={{ padding: 0, height: "100%" }}>
                    <div style={{ display: "flex", height: "100%" }}>
                        {/* Left Side - Booking Form */}
                        <div style={{ flex: 2, padding: "20px", overflowY: "auto" }}>
                            {this.renderBookingForm()}
                        </div>

                        {/* Right Side - Booking Summary */}
                        <div
                            style={{
                                flex: 1,
                                padding: "20px",
                                borderLeft: "1px solid var(--border-color)",
                            }}
                        >
                            {this.renderBookingSummary()}

                            {/* Action Buttons */}
                            <div
                                style={{
                                    marginTop: "var(--v3-spacing-lg)",
                                    display: "flex",
                                    gap: "var(--v3-spacing-md)",
                                }}
                            >
                                <Button
                                    large
                                    intent="primary"
                                    icon="tick"
                                    onClick={this.updateSurgery}
                                    loading={isUpdating}
                                    disabled={isUpdating}
                                    style={{ flex: 1 }}
                                >
                                    {isUpdating ? "Updating..." : "Update Surgery"}
                                </Button>
                                <Button
                                    large
                                    intent="none"
                                    icon="cross"
                                    onClick={this.hideEditForm}
                                    disabled={isUpdating}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        );
    };

    render() {
        const { showConfirmation, bookingReference, showCreateForm, showEditForm } =
            this.state;

        // Success Dialog
        if (showConfirmation) {
            return (
                <div
                    className="v3-page"
                    style={{
                        minHeight: "100vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Card
                        className="v3-card"
                        style={{
                            textAlign: "center",
                            maxWidth: "500px",
                            width: "100%",
                            padding: "var(--v3-spacing-xl)",
                        }}
                    >
                        <div
                            className="v3-success-icon"
                            style={{
                                width: "80px",
                                height: "80px",
                                borderRadius: "50%",
                                backgroundColor: "var(--success)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto var(--v3-spacing-lg) auto",
                            }}
                        >
                            <Icon icon={IconNames.TICK} size={40} color="white" />
                        </div>
                        <H1
                            className="v3-text-success"
                            style={{
                                marginBottom: "var(--v3-spacing-md)",
                                fontSize: "var(--v3-font-size-2xl)",
                            }}
                        >
                            Surgery Booked Successfully!
                        </H1>
                        <Text
                            className="v3-text-secondary"
                            style={{
                                fontSize: "var(--v3-font-size-lg)",
                                marginBottom: "var(--v3-spacing-xl)",
                            }}
                        >
                            Your surgery has been scheduled and confirmations have been sent.
                        </Text>

                        <Callout
                            intent="primary"
                            style={{ marginBottom: "var(--v3-spacing-xl)" }}
                        >
                            <Text className="v3-text-medium">
                                <strong>Booking Reference:</strong> {bookingReference}
                            </Text>
                        </Callout>

                        <div
                            style={{
                                display: "flex",
                                gap: "var(--v3-spacing-md)",
                                justifyContent: "center",
                                flexWrap: "wrap",
                            }}
                        >
                            <Button
                                large
                                intent="primary"
                                onClick={this.resetBooking}
                                icon={<Icon icon={IconNames.ADD} />}
                                className="v3-button v3-button-primary"
                            >
                                Book Another Surgery
                            </Button>
                            <Button
                                large
                                outlined
                                onClick={() => window.print()}
                                icon={<Icon icon={IconNames.PRINT} />}
                                className="v3-button v3-button-secondary"
                            >
                                Print Confirmation
                            </Button>
                        </div>
                    </Card>
                </div>
            );
        }

        return (
            <div>
                {/* Main Surgeries List View */}
                {this.renderSurgeriesList()}

                {/* Create Form Modal */}
                {this.renderCreateFormModal()}

                {/* Edit Form Modal */}
                {this.renderEditFormModal()}
            </div>
        );
    }
}

export default inject("authStore")(observer(withRouter(SurgeryBooking)));
